<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import AccessAssetsAPI from 'dashboard/api/accessAssets';
import ImovelAPI from 'dashboard/api/imoveis';
import { useMapGetter } from 'dashboard/composables/store';
import { useVisitasStore } from 'dashboard/stores/visitas';
import { useAlert, useTracking } from 'dashboard/composables';
import { trackQuintoEvent } from 'dashboard/helper/lais/trackingEvents';
import LaisActionReasonModal from 'dashboard/components-next/LaisActionReasonModal.vue';
import LaisCopyButton from 'dashboard/components-next/LaisCopyButton.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisInfoRow from 'dashboard/components-next/LaisInfoRow.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import DatePicker from 'dashboard/components-next/date-picker/DatePicker.vue';
import DateRangePicker from 'dashboard/components-next/date-picker/DateRangePicker.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import KeyCustodyDrawer from 'dashboard/components-next/keys/KeyCustodyDrawer.vue';
import AgendaTimeBlockCard from '../components/AgendaTimeBlockCard.vue';
import EntryInfoSearchPanel from '../components/EntryInfoSearchPanel.vue';
import KeySummaryCard from '../components/KeySummaryCard.vue';
import KeyTrackingCard from '../components/KeyTrackingCard.vue';
import PropertySuggestionCard from '../components/PropertySuggestionCard.vue';
import VisitOperationalSectionState from '../components/VisitOperationalSectionState.vue';
import {
  buildBulkBlockPayload,
  buildBlockPayloads,
  buildHourSlots,
  describePayloads,
  overlappingBlocks,
  sameDayTemporaryBlocks,
  schedulePresetsForSlots,
  slotIdsForSchedule,
  validateBlockDraft,
  weekdayLabel,
} from '../utils/agendaBlockUtils';

const props = defineProps({
  view: {
    type: String,
    required: true,
  },
});

const store = useVisitasStore();
const { track } = useTracking();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const currentRole = useMapGetter('getCurrentRole');
const todayIso = () => new Date().toISOString().slice(0, 10);
const addDaysIso = (baseIso, days) => {
  const [year, month, day] = String(baseIso || todayIso())
    .split('-')
    .map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
};
const currentUserId = computed(() => currentUser.value?.id || '');
const routeValue = value => (Array.isArray(value) ? value[0] : value);
const CANONICAL_SECTIONS = Object.freeze([
  {
    id: 'agenda',
    label: 'Agenda',
    icon: 'i-lucide-calendar-days',
    description:
      'Timeline de visitas e bloqueios com atualização independente.',
    emptyTitle: 'Agenda sem itens',
    emptyDescription:
      'Quando houver visitas ou bloqueios no período, eles aparecerão aqui.',
    errorTitle: 'Não foi possível carregar a agenda',
    unauthorizedTitle: 'Agenda restrita',
    unauthorizedDescription:
      'Seu perfil não possui acesso operacional à agenda de visitas.',
  },
  {
    id: 'chaves',
    label: 'Chaves',
    icon: 'i-lucide-key-round',
    description:
      'Retirada, posse, devolução e instruções de entrada por imóvel.',
    emptyTitle: 'Sem chaves em acompanhamento',
    emptyDescription:
      'Quando houver retirada, posse ou devolução pendente, ela aparecerá aqui.',
    errorTitle: 'Não foi possível carregar as chaves',
    unauthorizedTitle: 'Chaves restritas',
    unauthorizedDescription:
      'Seu perfil não possui acesso ao controle operacional de chaves.',
  },
  {
    id: 'alertas',
    label: 'Alertas e acompanhamento',
    icon: 'i-lucide-bell-ring',
    description:
      'Sinais de prazo, entrada, chave e conflito para acompanhamento diário.',
    emptyTitle: 'Sem alertas operacionais',
    emptyDescription:
      'Não há pendências críticas de agenda ou chaves no período acompanhado.',
    errorTitle: 'Não foi possível carregar os alertas',
    unauthorizedTitle: 'Alertas restritos',
    unauthorizedDescription:
      'Seu perfil não possui acesso aos alertas operacionais de visitas.',
  },
  {
    id: 'recuperacao',
    label: 'Recuperação e operação',
    icon: 'i-lucide-life-buoy',
    description:
      'Bloqueios, indisponibilidades e ações de recuperação da operação.',
    emptyTitle: 'Sem bloqueios ativos',
    emptyDescription:
      'Crie um bloqueio temporário quando o corretor não puder receber visitas.',
    errorTitle: 'Não foi possível carregar a operação',
    unauthorizedTitle: 'Operação restrita',
    unauthorizedDescription:
      'Seu perfil não possui acesso às ações de recuperação e bloqueio.',
  },
]);
const SECTION_IDS = new Set(CANONICAL_SECTIONS.map(section => section.id));
const SECTION_ALIASES = Object.freeze({
  agenda: 'agenda',
  schedule: 'agenda',
  calendario: 'agenda',
  chaves: 'chaves',
  keys: 'chaves',
  key: 'chaves',
  alertas: 'alertas',
  alerts: 'alertas',
  acompanhamento: 'alertas',
  followup: 'alertas',
  follow_up: 'alertas',
  recuperacao: 'recuperacao',
  recuperação: 'recuperacao',
  operacao: 'recuperacao',
  operação: 'recuperacao',
  operation: 'recuperacao',
  recovery: 'recuperacao',
  blocks: 'recuperacao',
});
const SECTION_BY_VIEW = Object.freeze({
  agenda: 'agenda',
  keys: 'chaves',
  blocks: 'recuperacao',
});
const normalizeSection = value => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase();
  return SECTION_ALIASES[normalized] || '';
};
const KEY_RETURN_MAX_DAYS = 6;
const KEY_PICKUP_LOOKBACK_DAYS = 6;
const keyPickupMaxDate = todayIso();
const keyPickupMinDate = addDaysIso(
  keyPickupMaxDate,
  -KEY_PICKUP_LOOKBACK_DAYS
);
const keyReturnMinDate = todayIso();
const keyReturnMaxDate = addDaysIso(keyReturnMinDate, KEY_RETURN_MAX_DAYS);
const keyPickupMomentOptions = [
  {
    id: 'before_first_visit',
    label: 'Antes da 1ª visita',
    description: 'Vou retirar a chave antes de encontrar o cliente.',
  },
  {
    id: 'on_first_visit',
    label: 'Na 1ª visita',
    description:
      'A retirada acontece no mesmo deslocamento da primeira visita.',
  },
  {
    id: 'after_first_visit',
    label: 'Depois da 1ª visita',
    description: 'A chave será retirada após validar a visita inicial.',
  },
];
const keyHolderBlockingStates = new Set([
  'with_agent',
  'withAgent',
  'to_return',
  'toReturn',
  'to_pick_up',
  'toPickUp',
]);
const ENTRY_INFO_STATUS_LABELS = {
  keysWithAgent: 'Chaves com o Corretor',
  keys_with_agent: 'Chaves com o Corretor',
  unavailable: 'Entrada indisponível',
  bookFirst: 'Agende a primeira visita',
  book_first: 'Agende a primeira visita',
  free: 'Entrada liberada',
};

const agenda = ref([]);
const blocks = ref([]);
const blockConfig = ref({});
const activeBlockTab = ref('temporary');
const keys = ref({});
const alertAgenda = ref([]);
const alertKeys = ref({});
const errorMessage = ref('');
const successMessage = ref('');
const sectionLastUpdatedAt = reactive({
  agenda: '',
  chaves: '',
  alertas: '',
  recuperacao: '',
});
const sectionStates = reactive(
  CANONICAL_SECTIONS.reduce((states, section) => {
    states[section.id] = {
      loading: false,
      error: '',
      status: '',
      unauthorized: false,
      loaded: false,
    };
    return states;
  }, {})
);
const sectionFromRoute = () =>
  normalizeSection(routeValue(route.query?.section)) ||
  SECTION_BY_VIEW[props.view] ||
  'agenda';
const activeSection = ref(sectionFromRoute());
const agendaStartDate = ref(todayIso());
const agendaDays = ref(8);
const showBlockModal = ref(false);
const showBlockCreateConfirm = ref(false);
const showBlockDeleteConfirm = ref(false);
const showKeyNotDeliveredReasonModal = ref(false);
const showBlockSameDayConfirm = ref(false);
const showBlockOverlapModal = ref(false);
const showBlockInfoModal = ref(false);
const isSavingBlock = ref(false);
const isDeletingBlock = ref(false);
const pendingBlockPayloads = ref([]);
const pendingBlockBulkPayload = ref(null);
const sameDayBlocksToReplace = ref([]);
const overlappingBlockMatches = ref([]);
const blockToDelete = ref(null);
const keyNotDeliveredTarget = ref(null);
const showKeyFormModal = ref(false);
const showKeyResultModal = ref(false);
const showKeyActionsDrawer = ref(false);
const showEntryInfoModal = ref(false);
const showKeyHolderDrawer = ref(false);
const showKeyCustodyDrawer = ref(false);
const selectedKeyHandover = ref(null);
const keyActionLoading = ref('');
const keyEntryInfo = ref(null);
const keyEntryInfoLoading = ref(false);
const keyEntryInfoError = ref('');
const keyHolderBlock = ref(null);
const keyCustodyImovelId = ref('');
const keyCustodyAssetId = ref('');
const isCheckingKeyHolder = ref(false);
const isMobileViewport = ref(false);
const entrySearchTerm = ref('');
const entrySearchError = ref('');
const keyResult = reactive({
  kind: 'success',
  title: '',
  description: '',
});

const blockDraft = reactive({
  userId: '',
  blockType: 'temporary',
  dayMode: 'single',
  dateRange: { start: todayIso(), end: todayIso() },
  weekday: 1,
  scheduleType: 'morning',
  selectedSlotIds: slotIdsForSchedule('morning'),
  reason: '',
});

const keyForm = reactive({
  imovel_id:
    routeValue(route.params?.imovelId) ||
    routeValue(route.query?.imovel_id) ||
    '',
  user_id: '',
  key_quantity: 1,
  key_type: 'COMMON',
  notes: '',
  confirm_pickup: true,
  pickup_moment: 'before_first_visit',
  picked_up_on: keyPickupMaxDate,
  due_return_on: keyReturnMaxDate,
});

const groupedKeys = computed(() => ({
  toPickUp: keys.value.toPickUp || [],
  delivered: keys.value.delivered || [],
  toReturn: keys.value.toReturn || [],
  notDelivered: keys.value.notDelivered || [],
}));
const keyGroupDefinitions = [
  {
    key: 'toReturn',
    title: 'A devolver',
    subtitle: 'Chaves com prazo ativo',
    tone: 'critical',
  },
  {
    key: 'toPickUp',
    title: 'A coletar',
    subtitle: 'Retiradas pendentes',
    tone: 'warning',
  },
  {
    key: 'delivered',
    title: 'Entregues',
    subtitle: 'Chaves já confirmadas',
    tone: 'primary',
  },
  {
    key: 'notDelivered',
    title: 'Não entregue',
    subtitle: 'Pendências com proprietário',
    tone: 'default',
  },
];

const activeSectionConfig = computed(
  () =>
    CANONICAL_SECTIONS.find(section => section.id === activeSection.value) ||
    CANONICAL_SECTIONS[0]
);
const isAgenda = computed(() => activeSection.value === 'agenda');
const isBlocks = computed(() => activeSection.value === 'recuperacao');
const isKeys = computed(() => activeSection.value === 'chaves');
const isAlerts = computed(() => activeSection.value === 'alertas');
const keyHolderDrawerSide = computed(() =>
  isMobileViewport.value ? 'bottom' : 'right'
);
const isAdministrator = computed(() => currentRole.value === 'administrator');
const isAgentRole = computed(() =>
  ['administrator', 'agent'].includes(String(currentRole.value || ''))
);
const hasPanelIdentity = computed(
  () => Boolean(currentUserId.value) || Boolean(currentRole.value)
);
const canViewSection = section => {
  if (!SECTION_IDS.has(section)) return false;
  if (!hasPanelIdentity.value) return false;
  if (section === 'recuperacao') return isAgentRole.value;
  return true;
};
const canManageRecovery = computed(() => canViewSection('recuperacao'));
const canManageKeys = computed(() => canViewSection('chaves'));
const canCreateRecurrentBlock = computed(() => isAdministrator.value);
const defaultUserId = () => blockDraft.userId || currentUserId.value;
const keyUserId = () => keyForm.user_id || currentUserId.value;
const blockConfigMinDate = computed(
  () => blockConfig.value.minDate || todayIso()
);
const blockConfigMaxDate = computed(
  () =>
    blockConfig.value.maxDate ||
    addDaysIso(blockConfigMinDate.value, blockConfig.value.maxDays || 60)
);
const blockHourSlots = computed(() => {
  const slots = blockConfig.value.hoursRange?.slots;
  if (Array.isArray(slots) && slots.length) {
    return slots.map(slot => ({
      id: slot.id,
      startTime: slot.startTime || slot.start_time,
      endTime: slot.endTime || slot.end_time,
      label: slot.label,
    }));
  }

  return buildHourSlots(blockConfig.value.hoursRange || {});
});
const blockSchedulePresets = computed(() => {
  const presets = schedulePresetsForSlots(blockHourSlots.value);
  const range = blockConfig.value.hoursRange || {};
  const startTime =
    range.startTime ||
    range.start_time ||
    presets.all_day.description.split(' - ')[0];
  const endTime =
    range.endTime ||
    range.end_time ||
    presets.all_day.description.split(' - ')[1];
  return {
    ...presets,
    all_day: {
      ...presets.all_day,
      description: `${startTime} - ${endTime}`,
    },
  };
});
const agendaSuspended = computed(
  () =>
    blockConfig.value.agendaSuspended === true ||
    blockConfig.value.agenda_suspended === true ||
    blockConfig.value.suspendedAgenda === true ||
    blockConfig.value.suspended_agenda === true
);
const weeklyAvailabilityRows = computed(
  () =>
    blockConfig.value.weeklyAvailability ||
    blockConfig.value.weekly_availability ||
    []
);
const blockTabOptions = [
  { id: 'temporary', label: 'Temporários' },
  { id: 'recurrent', label: 'Recorrentes' },
];
const temporaryBlocks = computed(() =>
  blocks.value.filter(block => block.blockType === 'temporary')
);
const recurrentBlocks = computed(() =>
  blocks.value.filter(block => block.blockType === 'recurrent')
);
const visibleBlocks = computed(() =>
  activeBlockTab.value === 'recurrent'
    ? recurrentBlocks.value
    : temporaryBlocks.value
);
const multiEndMinDate = computed(() =>
  addDaysIso(blockDraft.dateRange.start || blockConfigMinDate.value, 1)
);
const multiStartMaxDate = computed(() => {
  if (!blockDraft.dateRange.end) return blockConfigMaxDate.value;
  return addDaysIso(blockDraft.dateRange.end, -1);
});
const sectionUpdatedLabel = section => {
  const updatedAt = sectionLastUpdatedAt[section];
  if (!updatedAt) return '';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(updatedAt));
};
const activeSectionUpdatedLabel = computed(() =>
  sectionUpdatedLabel(activeSection.value)
);
const pendingBlockSummary = computed(() =>
  describePayloads(pendingBlockPayloads.value)
);
const draftPreviewPayloads = computed(() =>
  buildBlockPayloads(blockDraft, currentUserId.value, {
    hourSlots: blockHourSlots.value,
  })
);
const keyDueReturnValid = computed(() => {
  const value = keyForm.due_return_on;
  return value >= keyReturnMinDate && value <= keyReturnMaxDate;
});
const keyPickedUpDateValid = computed(() => {
  const value = keyForm.picked_up_on;
  return value >= keyPickupMinDate && value <= keyPickupMaxDate;
});
const canSubmitKeyHandover = computed(
  () =>
    Number(keyForm.imovel_id) > 0 &&
    Boolean(keyUserId()) &&
    Boolean(keyForm.pickup_moment) &&
    keyPickedUpDateValid.value &&
    keyDueReturnValid.value
);
const keyImovelTitle = computed(
  () => keyEntryInfo.value?.title || `Imóvel #${keyForm.imovel_id || '—'}`
);
const keyImovelAddress = computed(
  () => keyEntryInfo.value?.address || 'Endereço ainda não carregado'
);
const keyImovelImageUrl = computed(
  () =>
    keyEntryInfo.value?.imageUrl ||
    keyEntryInfo.value?.image_url ||
    keyEntryInfo.value?.imovel?.imageUrl ||
    keyEntryInfo.value?.imovel?.image_url ||
    ''
);
const keyShortCode = computed(
  () =>
    keyEntryInfo.value?.shortCode ||
    keyEntryInfo.value?.short_code ||
    keyEntryInfo.value?.code ||
    keyEntryInfo.value?.imovel?.code ||
    keyEntryInfo.value?.imovel?.codigo ||
    keyForm.imovel_id ||
    ''
);
const entryInfoRows = computed(() => [
  {
    label: 'Tipo de acesso',
    value: keyEntryInfo.value?.accessLabel || 'Não informado',
    icon: 'i-lucide-door-open',
  },
  {
    label: 'Instruções',
    value:
      keyEntryInfo.value?.instructions ||
      t('VISITS.OPERATIONAL.ENTRY_NO_INSTRUCTIONS'),
    icon: 'i-lucide-clipboard-list',
  },
  {
    label: 'Responsável',
    value: keyEntryInfo.value?.responsibleName || 'Responsável não informado',
    icon: 'i-lucide-user-round',
  },
  {
    label: 'Contato',
    value:
      keyEntryInfo.value?.responsiblePhone ||
      keyEntryInfo.value?.responsibleAddress ||
      'Contato não informado',
    icon: 'i-lucide-phone',
  },
]);
const entryInfoKeysWithAgent = computed(() =>
  Boolean(
    keyEntryInfo.value?.keysWithAgent || keyEntryInfo.value?.keys_with_agent
  )
);
const showEntryVisitHistory = computed(() => {
  const value =
    keyEntryInfo.value?.showVisitHistory ??
    keyEntryInfo.value?.show_visit_history;
  return value === undefined ? !entryInfoKeysWithAgent.value : Boolean(value);
});
const entryEntranceStatusLabel = computed(() => {
  const status =
    keyEntryInfo.value?.entranceStatus || keyEntryInfo.value?.entrance_status;
  return ENTRY_INFO_STATUS_LABELS[status] || '';
});
const entryVisitHistoryRows = computed(() =>
  Array.isArray(keyEntryInfo.value?.visitHistory)
    ? keyEntryInfo.value.visitHistory
    : []
);
const keySummaryCards = computed(() =>
  keyGroupDefinitions.slice(0, 3).map(group => ({
    ...group,
    count: groupedKeys.value[group.key].length,
  }))
);
const flattenedKeyHandovers = computed(() =>
  Object.values(groupedKeys.value).flatMap(value =>
    Array.isArray(value) ? value : []
  )
);
const alertGroupedKeys = computed(() => ({
  toPickUp: alertKeys.value.toPickUp || [],
  delivered: alertKeys.value.delivered || [],
  toReturn: alertKeys.value.toReturn || [],
  notDelivered: alertKeys.value.notDelivered || [],
}));
const flattenedAlertKeys = computed(() =>
  Object.values(alertGroupedKeys.value).flatMap(value =>
    Array.isArray(value) ? value : []
  )
);
const alertAgendaItems = computed(() =>
  alertAgenda.value.flatMap(day =>
    (day.items || []).map(item => ({
      ...item,
      dayLabel: day.label,
      dateId: day.dateId,
    }))
  )
);
const resetStatus = () => {
  errorMessage.value = '';
  successMessage.value = '';
};

const resetSectionState = section => {
  const state = sectionStates[section];
  if (!state) return;
  state.error = '';
  state.status = '';
  state.unauthorized = false;
};

const startSectionLoad = section => {
  const state = sectionStates[section];
  if (!state) return false;
  resetStatus();
  resetSectionState(section);
  if (!canViewSection(section)) {
    state.loading = false;
    state.loaded = true;
    state.unauthorized = true;
    return false;
  }
  state.loading = true;
  return true;
};

const finishSectionLoad = section => {
  const state = sectionStates[section];
  if (!state) return;
  state.loading = false;
  state.loaded = true;
};

const sectionErrorStatus = error =>
  error?.response?.status || error?.status || '';

const failSectionLoad = (section, error, fallbackMessage) => {
  const state = sectionStates[section];
  if (!state) return;
  const status = sectionErrorStatus(error);
  state.status = status;
  state.unauthorized = [401, 403].includes(Number(status));
  state.error = state.unauthorized ? '' : fallbackMessage;
};

const markSectionUpdated = section => {
  sectionLastUpdatedAt[section] = new Date().toISOString();
};

const activeSectionUrl = computed(() => {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.searchParams.set('section', activeSection.value);
  return url.toString();
});

const syncActiveSectionQuery = section => {
  router
    .replace({
      name: route.name || 'visitas_index',
      params: route.params,
      query: {
        ...route.query,
        section,
      },
    })
    .catch(() => {});
};

const selectPanelSection = section => {
  const nextSection = normalizeSection(section) || 'agenda';
  if (nextSection === activeSection.value) return;
  activeSection.value = nextSection;
  syncActiveSectionQuery(nextSection);
  try {
    trackQuintoEvent(track, 'visits_operational_panel_section_changed', {
      domain: 'visitas',
      section: nextSection,
    });
  } catch {
    // Tracking must not block operational tab changes.
  }
};

const trackBlockEvent = (name, properties = {}) => {
  try {
    track(name, {
      source: 'agenda_time_block_dialog',
      active_tab: activeBlockTab.value,
      user_id: defaultUserId(),
      ...properties,
    });
    trackQuintoEvent(track, name, {
      domain: 'time_block',
      active_tab: activeBlockTab.value,
      user_id: defaultUserId(),
      ...properties,
    });
  } catch {
    // Tracking must not block agenda operations.
  }
};

const trackEntryInfoEvent = (event, properties = {}) => {
  try {
    trackQuintoEvent(track, event, {
      domain: 'entry_info',
      imovel_id: keyForm.imovel_id || undefined,
      ...properties,
    });
  } catch {
    // Tracking must not block key operations.
  }
};

const trackKeyEvent = (event, properties = {}) => {
  try {
    const payload = {
      domain: 'keys_management',
      user_id: keyUserId(),
      ...properties,
    };
    track(event, payload);
    trackQuintoEvent(track, event, payload);
  } catch {
    // Tracking must not block key operations.
  }
};

const closeEntryInfoModal = () => {
  trackEntryInfoEvent('entrance_info_dialog_closed', {
    imovel_id: keyForm.imovel_id || undefined,
  });
  showEntryInfoModal.value = false;
};

const loadAgenda = async () => {
  if (!startSectionLoad('agenda')) return null;
  try {
    agenda.value = await store.agenda({
      start_date: agendaStartDate.value,
      days: agendaDays.value,
      user_id: currentUserId.value,
    });
    markSectionUpdated('agenda');
  } catch (error) {
    failSectionLoad('agenda', error, t('VISITS.ERRORS.LOAD_AGENDA'));
  } finally {
    finishSectionLoad('agenda');
  }
  return null;
};

const loadBlocks = async () => {
  if (!startSectionLoad('recuperacao')) return null;
  try {
    const userId = defaultUserId();
    const [configPayload, blockPayload] = await Promise.all([
      store.agentScheduleBlockConfig({ user_id: userId }),
      store.agentScheduleBlocks({
        user_id: userId,
        date: blockDraft.dateRange.start || agendaStartDate.value,
        filter_date: todayIso(),
      }),
    ]);
    blockConfig.value = configPayload;
    blocks.value = blockPayload;
    if (blockDraft.scheduleType !== 'custom') {
      blockDraft.selectedSlotIds = slotIdsForSchedule(
        blockDraft.scheduleType,
        [],
        blockHourSlots.value
      );
    }
    markSectionUpdated('recuperacao');
    trackBlockEvent('time_block_page_viewed', {
      blocks_count: blocks.value.length,
    });
  } catch (error) {
    failSectionLoad('recuperacao', error, t('VISITS.ERRORS.LOAD_BLOCKS'));
  } finally {
    finishSectionLoad('recuperacao');
  }
  return null;
};

const loadKeys = async () => {
  if (!startSectionLoad('chaves')) return null;
  try {
    keys.value = await store.agentKeys({ user_id: keyUserId() });
    markSectionUpdated('chaves');
    trackKeyEvent('keys_management_page_viewed', {
      keys_to_collect_total: groupedKeys.value.toPickUp.length,
      assigned_keys_total: groupedKeys.value.delivered.length,
      keys_to_return_total: groupedKeys.value.toReturn.length,
    });
    trackKeyEvent('keys_management_keys_to_collect_page_viewed', {
      total: groupedKeys.value.toPickUp.length,
    });
    trackKeyEvent('keys_management_assigned_keys_page_viewed', {
      total: groupedKeys.value.delivered.length,
    });
    trackKeyEvent('keys_management_keys_to_return_page_viewed', {
      total: groupedKeys.value.toReturn.length,
    });
    trackEntryInfoEvent('entrance_info_page_viewed', {
      source: 'keys_panel',
    });
  } catch (error) {
    failSectionLoad('chaves', error, t('VISITS.ERRORS.LOAD_KEYS'));
  } finally {
    finishSectionLoad('chaves');
  }
  return null;
};

const loadAlerts = async () => {
  if (!startSectionLoad('alertas')) return null;
  try {
    const [agendaPayload, keysPayload] = await Promise.all([
      store.agenda({
        start_date: todayIso(),
        days: 3,
        user_id: currentUserId.value,
      }),
      store.agentKeys({ user_id: keyUserId() }),
    ]);
    alertAgenda.value = agendaPayload;
    alertKeys.value = keysPayload;
    markSectionUpdated('alertas');
    try {
      trackQuintoEvent(track, 'visits_operational_alerts_viewed', {
        domain: 'visitas',
        alerts_count:
          flattenedAlertKeys.value.length + alertAgendaItems.value.length,
      });
    } catch {
      // Tracking must not block alert reads.
    }
  } catch (error) {
    failSectionLoad('alertas', error, t('VISITS.ERRORS.LOAD_AGENDA'));
  } finally {
    finishSectionLoad('alertas');
  }
  return null;
};

const loadActiveSection = () => {
  if (isAgenda.value) return loadAgenda();
  if (isKeys.value) return loadKeys();
  if (isAlerts.value) return loadAlerts();
  if (isBlocks.value) return loadBlocks();
  return null;
};

const resetBlockDraft = () => {
  blockDraft.userId = currentUserId.value || '';
  blockDraft.blockType = 'temporary';
  blockDraft.dayMode = 'single';
  blockDraft.dateRange = { start: todayIso(), end: todayIso() };
  blockDraft.weekday = 1;
  blockDraft.scheduleType = 'morning';
  blockDraft.selectedSlotIds = slotIdsForSchedule(
    'morning',
    [],
    blockHourSlots.value
  );
  blockDraft.reason = '';
  pendingBlockPayloads.value = [];
  pendingBlockBulkPayload.value = null;
  sameDayBlocksToReplace.value = [];
  overlappingBlockMatches.value = [];
};

const openBlockModal = () => {
  resetStatus();
  if (!canManageRecovery.value) {
    sectionStates.recuperacao.unauthorized = true;
    return;
  }
  trackBlockEvent('time_block_add_clicked');
  trackBlockEvent('time_block_action_clicked', { action: 'add' });
  trackBlockEvent('time_block_new_block_clicked');
  trackBlockEvent('TIME_BLOCK_NEW_BLOCK_CLICKED');
  if (agendaSuspended.value) {
    useAlert('Agenda suspensa. Entre em contato com o suporte para reativar.');
    return;
  }
  resetBlockDraft();
  showBlockModal.value = true;
};

const openBlockInfoModal = () => {
  if (!canViewSection('recuperacao')) {
    sectionStates.recuperacao.unauthorized = true;
    return;
  }
  showBlockInfoModal.value = true;
  trackBlockEvent('time_block_action_clicked', { action: 'info' });
  trackBlockEvent('time_block_page_info_clicked');
  trackBlockEvent('TIME_BLOCK_PAGE_INFO_CLICKED');
};

const setBlockTab = tab => {
  activeBlockTab.value = tab;
  trackBlockEvent('time_block_tab_changed', { tab });
  trackBlockEvent('TIME_BLOCK_TAB_CHANGED', { tab });
};

const setBlockType = value => {
  if (value === 'recurrent' && !canCreateRecurrentBlock.value) {
    blockDraft.blockType = 'temporary';
    useAlert(
      'Para adicionar um bloqueio recorrente, entre em contato com o suporte.'
    );
    trackBlockEvent('time_block_recurrent_support_alert_viewed');
    return;
  }
  blockDraft.blockType = value;
};

const setBlockDayMode = value => {
  blockDraft.dayMode = value;
  if (value === 'single') blockDraft.dateRange.end = blockDraft.dateRange.start;
};

const setScheduleType = value => {
  blockDraft.scheduleType = value;
  if (value !== 'custom') {
    blockDraft.selectedSlotIds = slotIdsForSchedule(
      value,
      [],
      blockHourSlots.value
    );
  }
};

const isHourSelected = slotId => blockDraft.selectedSlotIds.includes(slotId);

const toggleHourSlot = slotId => {
  if (blockDraft.scheduleType !== 'custom') return;

  if (isHourSelected(slotId)) {
    blockDraft.selectedSlotIds = blockDraft.selectedSlotIds.filter(
      id => id !== slotId
    );
    return;
  }

  blockDraft.selectedSlotIds = [...blockDraft.selectedSlotIds, slotId].sort();
};

const prepareBlockSubmission = () => {
  resetStatus();
  if (agendaSuspended.value) {
    useAlert('Agenda suspensa. Não é possível criar novos bloqueios.');
    return false;
  }

  const errors = validateBlockDraft(blockDraft, currentUserId.value, {
    minDate: blockConfigMinDate.value,
    maxDate: blockConfigMaxDate.value,
    maxDays: blockConfig.value.maxDays,
    allowRecurrent: canCreateRecurrentBlock.value,
    hourSlots: blockHourSlots.value,
  });
  if (errors.length) {
    useAlert(errors[0]);
    return false;
  }

  const payloads = buildBlockPayloads(blockDraft, currentUserId.value, {
    hourSlots: blockHourSlots.value,
  });
  const bulkPayload = buildBulkBlockPayload(blockDraft, currentUserId.value, {
    hourSlots: blockHourSlots.value,
  });
  const sameDayMatches = sameDayTemporaryBlocks(blocks.value, payloads);
  const overlapMatches = overlappingBlocks(
    blocks.value,
    payloads,
    sameDayMatches.map(block => block.id)
  );

  pendingBlockPayloads.value = payloads;
  pendingBlockBulkPayload.value = bulkPayload;
  sameDayBlocksToReplace.value = sameDayMatches;
  overlappingBlockMatches.value = overlapMatches;
  trackBlockEvent('create-time-block-request', {
    startDate: bulkPayload.starts_on,
    endDate: bulkPayload.ends_on,
    isEntireDay: bulkPayload.is_entire_day,
    scheduleType: bulkPayload.schedule_type,
    blockType: bulkPayload.block_type,
    blockTypesByDay: payloads.map(payload => payload.block_type),
  });

  if (overlapMatches.length) {
    showBlockOverlapModal.value = true;
    return false;
  }

  if (sameDayMatches.length) {
    showBlockSameDayConfirm.value = true;
    return false;
  }

  showBlockCreateConfirm.value = true;
  return true;
};

const createPendingBlocks = async ({ replaceSameDay = false } = {}) => {
  if (!pendingBlockPayloads.value.length || !pendingBlockBulkPayload.value)
    return;

  isSavingBlock.value = true;
  try {
    const response = await store.bulkCreateAgentScheduleBlocks({
      ...pendingBlockBulkPayload.value,
      replace_block_ids: replaceSameDay
        ? sameDayBlocksToReplace.value.map(block => block.id)
        : [],
    });
    const affectedCount = response.affectedVisitsCount || 0;
    successMessage.value = affectedCount
      ? `Bloqueio criado. ${affectedCount} visita(s) foram enviadas para realocação.`
      : 'Bloqueio criado. O corretor deixará de receber visitas nesse período.';
    useAlert(successMessage.value);
    trackBlockEvent('time_block_confirmed', {
      affected_count: affectedCount,
      replace_same_day: replaceSameDay,
    });
    showBlockModal.value = false;
    showBlockCreateConfirm.value = false;
    showBlockSameDayConfirm.value = false;
    resetBlockDraft();
    await loadBlocks();
  } catch {
    errorMessage.value = t('VISITS.ERRORS.CREATE_BLOCK');
    useAlert(errorMessage.value);
  } finally {
    isSavingBlock.value = false;
  }
};

const requestDeleteBlock = block => {
  if (!canManageRecovery.value) {
    sectionStates.recuperacao.unauthorized = true;
    return;
  }
  blockToDelete.value = block;
  showBlockDeleteConfirm.value = true;
  trackBlockEvent('time_block_action_clicked', {
    action: 'delete',
    block_id: block?.id,
  });
  trackBlockEvent('time_block_delete_clicked', {
    block_id: block?.id,
    scheduleType: block?.durationType || block?.duration_type,
    blockType: block?.blockType || block?.block_type,
  });
};

const deleteBlock = async ({ reason = '' } = {}) => {
  if (!blockToDelete.value?.id) return;

  isDeletingBlock.value = true;
  resetStatus();
  try {
    trackBlockEvent('time_block_delete_reason_confirmed', {
      block_id: blockToDelete.value.id,
      reason_length: reason.length,
    });
    await store.deleteAgentScheduleBlock(blockToDelete.value.id);
    successMessage.value =
      'Bloqueio excluído. O corretor voltará a receber visitas nesse período.';
    useAlert(successMessage.value);
    showBlockDeleteConfirm.value = false;
    blockToDelete.value = null;
    await loadBlocks();
  } catch {
    errorMessage.value = t('VISITS.ERRORS.DELETE_BLOCK');
    useAlert(errorMessage.value);
  } finally {
    isDeletingBlock.value = false;
  }
};

const showKeyResult = ({ kind = 'success', title, description }) => {
  keyResult.kind = kind;
  keyResult.title = title;
  keyResult.description = description;
  showKeyResultModal.value = true;
};

const openKeyFormModal = () => {
  resetStatus();
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  showKeyFormModal.value = true;
};

const retryKeyResult = () => {
  showKeyResultModal.value = false;
  openKeyFormModal();
};

const closeKeyFormModal = () => {
  if (keyActionLoading.value) return;
  showKeyFormModal.value = false;
};

const resetKeyForm = () => {
  keyForm.imovel_id = '';
  keyForm.key_quantity = 1;
  keyForm.key_type = 'COMMON';
  keyForm.notes = '';
  keyForm.confirm_pickup = true;
  keyForm.pickup_moment = 'before_first_visit';
  keyForm.picked_up_on = keyPickupMaxDate;
  keyForm.due_return_on = keyReturnMaxDate;
  keyEntryInfo.value = null;
  keyEntryInfoError.value = '';
};

const dateFromIso = value => {
  const [year, month, day] = String(value || '')
    .split('-')
    .map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const dueInDaysFromReturnDate = (value, startIso = keyForm.picked_up_on) => {
  const start = dateFromIso(startIso);
  const end = dateFromIso(value);
  if (!start || !end) return 1;

  const diff = Math.ceil((end.getTime() - start.getTime()) / 86400000);
  return Math.max(1, diff || 1);
};

const normalizeEntryInfoPayload = payload =>
  camelcaseKeys(payload || {}, { deep: true });

const entryInfoErrorMessage = error => {
  const status = error?.response?.status || error?.status;
  if (status === 403)
    return 'Este imóvel está fora da sua região de atendimento.';
  if (status === 404) return 'Não encontramos imóvel com esse código.';

  return t('VISITS.ERRORS.LOAD_ENTRY_INFO');
};

const loadKeyEntryInfo = async ({ open = true } = {}) => {
  const imovelId = Number(keyForm.imovel_id);
  keyEntryInfoError.value = '';
  if (!imovelId) {
    keyEntryInfo.value = null;
    return null;
  }

  keyEntryInfoLoading.value = true;
  if (open) showEntryInfoModal.value = true;
  try {
    const { data } = await ImovelAPI.entryInformation(imovelId);
    keyEntryInfo.value = normalizeEntryInfoPayload(data.payload || data);
    return keyEntryInfo.value;
  } catch (error) {
    keyEntryInfo.value = null;
    keyEntryInfoError.value = entryInfoErrorMessage(error);
    return null;
  } finally {
    keyEntryInfoLoading.value = false;
  }
};

const searchEntryInformation = async ({ imovelId } = {}) => {
  const target = Number(imovelId || entrySearchTerm.value);
  entrySearchError.value = '';
  if (!target) {
    entrySearchError.value = 'Informe um código de imóvel válido.';
    useAlert(entrySearchError.value);
    trackEntryInfoEvent('entrance_info_invalid_id', { reason: 'empty' });
    return;
  }

  keyForm.imovel_id = String(target);
  entrySearchTerm.value = String(target);
  trackEntryInfoEvent('entrance_info_find_property_clicked', {
    imovel_id: target,
  });
  trackEntryInfoEvent('entrance_info_search_submitted', {
    imovel_id: target,
  });
  const info = await loadKeyEntryInfo({ open: true });
  if (!info) {
    entrySearchError.value =
      keyEntryInfoError.value || 'Não encontramos instruções para este imóvel.';
    useAlert(entrySearchError.value);
    trackEntryInfoEvent(
      entrySearchError.value.includes('fora da sua região')
        ? 'entrance_info_outside_region'
        : 'entrance_info_invalid_id',
      { imovel_id: target }
    );
    return;
  }

  const isOutsideRegion = info.outsideRegion || info.outside_region;
  trackEntryInfoEvent(
    isOutsideRegion
      ? 'entrance_info_outside_region'
      : 'entrance_info_property_viewed',
    { imovel_id: target, source: 'entry_search' }
  );
};

const selectEntrySuggestion = suggestion => {
  entrySearchTerm.value = String(suggestion.id || '');
  searchEntryInformation({ imovelId: suggestion.id });
};

const flattenKeyHandovers = payload =>
  Object.values(payload || {}).flatMap(value =>
    Array.isArray(value) ? value : []
  );

const keyImovelId = item =>
  item?.imovelId ||
  item?.imovel_id ||
  item?.imovel?.id ||
  item?.imovel?.imovelId;

const keyUserName = item =>
  item?.currentCustodian?.name ||
  item?.current_custodian?.name ||
  item?.user?.name ||
  item?.userName ||
  item?.user?.email ||
  'outro corretor';

const keyOwnerName = item =>
  item?.ownerName ||
  item?.owner_name ||
  item?.owner?.name ||
  item?.proprietario?.nome ||
  item?.imovel?.ownerName ||
  item?.imovel?.owner_name ||
  'Proprietário não informado';

const keyAddress = item =>
  item?.imovel?.endereco ||
  item?.imovel?.address ||
  item?.address ||
  item?.location ||
  '';

const firstImageUrl = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.url || value.imageUrl || value.image_url || '';
};

const keyImageUrl = item => {
  const imovel = item?.imovel || {};
  const photos = imovel.photos || imovel.images || imovel.media || [];
  return (
    item?.imageUrl ||
    item?.image_url ||
    imovel.imageUrl ||
    imovel.image_url ||
    imovel.coverImage ||
    imovel.cover_image ||
    firstImageUrl(Array.isArray(photos) ? photos[0] : photos)
  );
};

const keyShortCodeForItem = item =>
  item?.shortCode ||
  item?.short_code ||
  item?.code ||
  item?.imovel?.code ||
  item?.imovel?.codigo ||
  keyImovelId(item) ||
  '';

const keyAccessAssetId = item =>
  item?.accessAssetId ||
  item?.access_asset_id ||
  item?.accessAsset?.id ||
  item?.access_asset?.id ||
  '';

const normalizeAccessAssets = payload =>
  camelcaseKeys(payload || [], { deep: true });

const findBlockingAccessAsset = async (imovelId, userId) => {
  try {
    const { data } = await AccessAssetsAPI.forImovel(imovelId, {
      status: 'blocked',
    });
    return normalizeAccessAssets(data.payload || data).find(asset => {
      const custodian = asset.currentCustodian || {};
      return String(custodian.id || '') !== String(userId || '');
    });
  } catch {
    return null;
  }
};

const findBlockingKeyHolder = async (imovelId, userId) => {
  isCheckingKeyHolder.value = true;
  try {
    const payload = await store.agentKeys({});
    return flattenKeyHandovers(payload).find(item => {
      const itemImovelId = item.imovelId || item.imovel_id;
      const itemUserId = item.userId || item.user_id;
      return (
        String(itemImovelId) === String(imovelId) &&
        String(itemUserId) !== String(userId) &&
        keyHolderBlockingStates.has(item.state)
      );
    });
  } finally {
    isCheckingKeyHolder.value = false;
  }
};

const openKeyHolderDrawer = block => {
  keyHolderBlock.value = block;
  showKeyHolderDrawer.value = true;
  try {
    track('key_holder_lock_drawer_viewed', {
      imovel_id: block?.imovelId || block?.imovel_id,
      handover_id: block?.id,
      state: block?.state,
    });
  } catch {
    // Tracking must not block key operations.
  }
};

const openKeyCustodyDrawer = (item = {}) => {
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  const imovelId = keyImovelId(item) || keyForm.imovel_id;
  if (!imovelId) {
    useAlert('Informe ou selecione um imóvel para abrir o ledger.');
    return;
  }

  keyCustodyImovelId.value = imovelId;
  keyCustodyAssetId.value = keyAccessAssetId(item);
  showKeyCustodyDrawer.value = true;
  showKeyActionsDrawer.value = false;
  try {
    trackKeyEvent('keys_management_custody_ledger_opened', {
      imovel_id: imovelId,
      access_asset_id: keyCustodyAssetId.value || undefined,
    });
  } catch {
    // Tracking must not block key operations.
  }
};

const handleKeyCustodyChanged = async () => {
  useAlert('Ledger de chaves atualizado.');
  await loadKeys();
};

const createKeyHandover = async () => {
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  resetStatus();
  const imovelId = Number(keyForm.imovel_id);
  const userId = keyUserId();
  if (!canSubmitKeyHandover.value) {
    errorMessage.value =
      'Informe imóvel, corretor, momento da retirada, data da retirada e uma data de devolução em até 6 dias.';
    showKeyResult({
      kind: 'error',
      title: 'Ops, aconteceu um imprevisto',
      description: errorMessage.value,
    });
    return;
  }

  keyActionLoading.value = 'create';
  try {
    trackKeyEvent('keys_management_keys_to_pick_up_confirm_button_clicked', {
      imovel_id: imovelId,
      got_key: keyForm.pickup_moment,
    });
    await loadKeyEntryInfo({ open: false });
    const blockedAsset = await findBlockingAccessAsset(imovelId, userId);
    if (blockedAsset) {
      showKeyFormModal.value = false;
      openKeyHolderDrawer({
        ...blockedAsset,
        imovelId,
        state: 'blocked',
        user: blockedAsset.currentCustodian,
        accessAssetId: blockedAsset.id,
      });
      return;
    }
    const keyHolder = await findBlockingKeyHolder(imovelId, userId);
    if (keyHolder) {
      showKeyFormModal.value = false;
      openKeyHolderDrawer(keyHolder);
      return;
    }

    await store.createKeyHandover(
      imovelId,
      {
        user_id: userId,
        key_quantity: keyForm.key_quantity,
        key_type: keyForm.key_type,
        notes: keyForm.notes,
        comments: keyForm.notes,
        got_key: keyForm.pickup_moment,
        when_agent_got_key: keyForm.pickup_moment,
        business_context: 'RENT',
        delivered_on: keyForm.picked_up_on,
        expected_return_on: keyForm.due_return_on,
        metadata: {
          pickup_moment: keyForm.pickup_moment,
          got_key: keyForm.pickup_moment,
          when_agent_got_key: keyForm.pickup_moment,
          business_context: 'RENT',
          delivered_on: keyForm.picked_up_on,
          expected_return_on: keyForm.due_return_on,
          comments: keyForm.notes,
        },
      },
      {
        confirm_pickup: keyForm.confirm_pickup,
        due_in_days: dueInDaysFromReturnDate(keyForm.due_return_on),
      }
    );
    successMessage.value = 'Chave registrada.';
    resetKeyForm();
    showKeyFormModal.value = false;
    await loadKeys();
    showKeyResult({
      title: 'Recebemos a sua confirmação',
      description:
        'A retirada das chaves foi registrada e já aparece em Minhas Chaves.',
    });
    trackKeyEvent('keys_management_keys_to_pick_up_page_success_viewed', {
      imovel_id: imovelId,
    });
  } catch {
    errorMessage.value = t('VISITS.ERRORS.REGISTER_KEY');
    showKeyResult({
      kind: 'error',
      title: 'Ops, aconteceu um imprevisto',
      description: errorMessage.value,
    });
  } finally {
    keyActionLoading.value = '';
  }
};

const returnKey = async id => {
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  resetStatus();
  keyActionLoading.value = `return-${id}`;
  try {
    await store.returnKeyHandover(id);
    successMessage.value = 'Devolução confirmada.';
    await loadKeys();
    showKeyActionsDrawer.value = false;
    showKeyResult({
      title: 'Devolução confirmada',
      description: 'A chave saiu da sua lista de pendências de devolução.',
    });
  } catch {
    errorMessage.value = t('VISITS.ERRORS.CONFIRM_RETURN');
    showKeyResult({
      kind: 'error',
      title: 'Ops, aconteceu um imprevisto',
      description: errorMessage.value,
    });
  } finally {
    keyActionLoading.value = '';
  }
};

const pickupKey = async id => {
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  resetStatus();
  keyActionLoading.value = `pickup-${id}`;
  try {
    await store.pickupKeyHandover(id, {
      delivered_on: todayIso(),
      got_key: 'on_first_visit',
      business_context: 'RENT',
    });
    successMessage.value = 'Retirada confirmada.';
    await loadKeys();
    showKeyActionsDrawer.value = false;
    showKeyResult({
      title: 'Recebemos a sua confirmação',
      description: 'Vamos verificar com o proprietário se está tudo certo.',
    });
  } catch {
    errorMessage.value = t('VISITS.ERRORS.CONFIRM_PICKUP');
    showKeyResult({
      kind: 'error',
      title: 'Ops, aconteceu um imprevisto',
      description: errorMessage.value,
    });
  } finally {
    keyActionLoading.value = '';
  }
};

const requestMarkKeyNotDelivered = item => {
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  keyNotDeliveredTarget.value = item;
  showKeyNotDeliveredReasonModal.value = true;
};

const markKeyNotDelivered = async ({ reason = '' } = {}) => {
  const item = keyNotDeliveredTarget.value;
  const id = item?.id;
  if (!id) return;
  resetStatus();
  keyActionLoading.value = `not-delivered-${id}`;
  try {
    await store.notDeliveredKeyHandover(id, {
      reason: reason || 'agent_reported_not_delivered',
      notes: reason,
    });
    successMessage.value = 'Chave marcada como não entregue.';
    await loadKeys();
    showKeyActionsDrawer.value = false;
    showKeyNotDeliveredReasonModal.value = false;
    keyNotDeliveredTarget.value = null;
    showKeyResult({
      title: 'Pendência registrada',
      description:
        'Marcamos esta chave como não entregue para acompanhamento com o proprietário.',
    });
  } catch {
    errorMessage.value = t('VISITS.ERRORS.CONFIRM_PICKUP');
    showKeyResult({
      kind: 'error',
      title: 'Ops, aconteceu um imprevisto',
      description: errorMessage.value,
    });
  } finally {
    keyActionLoading.value = '';
  }
};

const openKeyActions = item => {
  if (!canManageKeys.value) {
    sectionStates.chaves.unauthorized = true;
    return;
  }
  selectedKeyHandover.value = item;
  showKeyActionsDrawer.value = true;
};

const explainOwnerContactFromKey = () => {
  trackKeyEvent('keys_management_chat_with_owner_clicked', {
    imovel_id: keyImovelId(selectedKeyHandover.value),
    handover_id: selectedKeyHandover.value?.id,
  });
  showKeyActionsDrawer.value = false;
  showKeyHolderDrawer.value = false;
  useAlert(
    'Abra o imóvel para solicitar o contato do proprietário com auditoria.'
  );
};

const formatDateTime = value => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const formatTime = value => {
  if (!value) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const hourLabel = hour => `${String(hour).padStart(2, '0')}:00`;

const isTodayDateId = dateId => dateId === todayIso();

const agendaTimelineRows = day => {
  const items = [...(day.items || [])].sort(
    (a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()
  );
  const hourMap = items.reduce((acc, item) => {
    const hour = new Date(item.startsAt).getHours();
    acc[hour] = acc[hour] || [];
    acc[hour].push(item);
    return acc;
  }, {});
  const hours = Object.keys(hourMap)
    .map(Number)
    .sort((a, b) => a - b);
  const rows = [];
  const currentHour = new Date().getHours();
  let nowInserted = false;

  hours.forEach(hour => {
    if (isTodayDateId(day.dateId) && !nowInserted && currentHour <= hour) {
      rows.push({ type: 'now', id: `now-${day.dateId}` });
      nowInserted = true;
    }

    rows.push({
      type: 'hour',
      id: `${day.dateId}-${hour}`,
      hour,
      items: hourMap[hour],
    });
  });

  if (isTodayDateId(day.dateId) && !nowInserted) {
    rows.push({ type: 'now', id: `now-${day.dateId}` });
  }

  return rows;
};

const hasEntryInfoDetails = computed(
  () =>
    Boolean(keyEntryInfo.value?.accessLabel) ||
    Boolean(keyEntryInfo.value?.instructions) ||
    Boolean(keyEntryInfo.value?.responsibleName) ||
    Boolean(keyEntryInfo.value?.responsiblePhone) ||
    Boolean(keyEntryInfo.value?.responsibleAddress)
);

const itemTitle = item =>
  item.title || item.imovel?.titulo || item.reason || 'Item da agenda';

const keyTitle = item =>
  item?.imovel?.titulo ||
  item?.imovel?.title ||
  item?.title ||
  (item?.imovelId || item?.imovel_id
    ? `Imóvel #${item.imovelId || item.imovel_id}`
    : 'Imóvel');
const entrySearchSuggestions = computed(() => {
  const seen = new Set();
  return flattenedKeyHandovers.value
    .map(item => {
      const id = keyImovelId(item);
      if (!id || seen.has(String(id))) return null;
      seen.add(String(id));
      return {
        id: String(id),
        label: `${keyTitle(item)} · #${id}`,
      };
    })
    .filter(Boolean)
    .slice(0, 5);
});
const keyDue = item =>
  item.dueReturnAt ||
  item.due_return_at ||
  item.dueReturnOn ||
  item.due_return_on ||
  item.returnedAt ||
  item.returned_at ||
  item.pickedUpAt ||
  item.picked_up_at;
const keyActionLoadingId = item => {
  if (['to_pick_up', 'toPickUp'].includes(item?.state)) {
    return `pickup-${item.id}`;
  }
  if (['to_return', 'toReturn'].includes(item?.state)) {
    return `return-${item.id}`;
  }
  return '';
};
const startOfDay = value => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};
const keyUrgency = item => {
  const due = startOfDay(keyDue(item));
  if (!due) return { key: 'noDate', label: 'Sem prazo definido', order: 4 };
  const today = startOfDay(keyReturnMinDate);
  const diff = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return { key: 'overdue', label: 'Atrasada', order: 1 };
  if (diff === 0) return { key: 'today', label: 'Vence hoje', order: 2 };
  return { key: 'upcoming', label: `Em ${diff} dia(s)`, order: 3 };
};
const sortedKeys = items =>
  [...(items || [])].sort((a, b) => {
    const urgencyA = keyUrgency(a);
    const urgencyB = keyUrgency(b);
    if (urgencyA.order !== urgencyB.order)
      return urgencyA.order - urgencyB.order;
    return (
      new Date(keyDue(a) || 0).getTime() - new Date(keyDue(b) || 0).getTime()
    );
  });
const keySectionSegments = groupKey => {
  const items = sortedKeys(groupedKeys.value[groupKey]);
  if (groupKey !== 'toReturn') {
    return [{ id: groupKey, title: 'Todos', items }];
  }

  return [
    { id: 'overdue', title: 'Atrasadas', items: [] },
    { id: 'today', title: 'Vencem hoje', items: [] },
    { id: 'upcoming', title: 'No prazo', items: [] },
    { id: 'noDate', title: 'Sem prazo', items: [] },
  ]
    .map(segment => ({
      ...segment,
      items: items.filter(item => keyUrgency(item).key === segment.id),
    }))
    .filter(segment => segment.items.length);
};
const scrollToKeySection = key => {
  if (typeof document === 'undefined') return;
  document
    .getElementById(`keys-section-${key}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
const scrollToEntrySearch = () => {
  if (typeof document === 'undefined') return;
  document
    .getElementById('entry-info-search')
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
const openEntryInfoForKey = item => {
  const imovelId = keyImovelId(item);
  if (!imovelId) {
    useAlert('Este registro ainda não tem código de imóvel para consulta.');
    trackEntryInfoEvent('entrance_info_invalid_id', { source: 'key_card' });
    return;
  }
  trackEntryInfoEvent('entrance_info_property_viewed', {
    imovel_id: imovelId,
    source: 'key_card',
  });
  searchEntryInformation({ imovelId });
};
const showKeysInfo = () => {
  useAlert(
    'Use os cards para acompanhar retiradas, devoluções e instruções de entrada por imóvel.'
  );
};
const goBackFromKeys = () => {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    router.back();
    return;
  }
  useAlert('Você já está em Minhas Chaves.');
};
const suggestPropertyFromKeys = () => {
  trackKeyEvent('keys_management_property_indication_card_clicked');
  useAlert(
    'Indicação registrada como intenção. Abra a ficha do imóvel para anexar dados e fotos.'
  );
};
const blockDateLabel = block => {
  if (block.blockType === 'recurrent') return weekdayLabel(block.weekday);
  if (!block.startsOn) return 'Sem data';
  return block.startsOn === block.endsOn || !block.endsOn
    ? block.startsOn
    : `${block.startsOn} até ${block.endsOn}`;
};
const blockDeleteDescription = computed(() => {
  if (!blockToDelete.value) {
    return 'Informe o motivo para remover este bloqueio de agenda.';
  }

  return `Ao excluir o bloqueio de ${blockDateLabel(blockToDelete.value)} (${blockToDelete.value.startTime}-${blockToDelete.value.endTime}), o corretor voltará a receber visitas nesse período.`;
});

const operationalAlerts = computed(() => {
  const rows = [];

  flattenedAlertKeys.value.forEach(item => {
    const urgency = keyUrgency(item);
    if (
      ['to_return', 'toReturn'].includes(item?.state) ||
      urgency.key === 'overdue' ||
      urgency.key === 'today'
    ) {
      rows.push({
        id: `key-return-${item.id}`,
        title: keyTitle(item),
        description: `Devolução de chave: ${urgency.label}`,
        meta: keyAddress(item) || `Imóvel #${keyImovelId(item) || '—'}`,
        icon: 'i-lucide-key-round',
        tone: urgency.key === 'overdue' ? 'critical' : 'warning',
        sort: urgency.order,
        action: () => openKeyActions(item),
      });
    } else if (['to_pick_up', 'toPickUp'].includes(item?.state)) {
      rows.push({
        id: `key-pickup-${item.id}`,
        title: keyTitle(item),
        description: 'Retirada de chave pendente para acompanhamento.',
        meta: keyAddress(item) || `Imóvel #${keyImovelId(item) || '—'}`,
        icon: 'i-lucide-key-square',
        tone: 'warning',
        sort: 3,
        action: () => openKeyActions(item),
      });
    } else if (['not_delivered', 'notDelivered'].includes(item?.state)) {
      rows.push({
        id: `key-not-delivered-${item.id}`,
        title: keyTitle(item),
        description: 'Chave marcada como não entregue pelo corretor.',
        meta: keyAddress(item) || `Imóvel #${keyImovelId(item) || '—'}`,
        icon: 'i-lucide-triangle-alert',
        tone: 'critical',
        sort: 1,
        action: () => openKeyActions(item),
      });
    }
  });

  alertAgendaItems.value.forEach(item => {
    const startsAt = new Date(item.startsAt || item.starts_at || 0);
    const isToday =
      item.dateId === todayIso() ||
      (!Number.isNaN(startsAt.getTime()) &&
        startsAt.toISOString().slice(0, 10) === todayIso());

    if (item.type === 'block') {
      rows.push({
        id: `agenda-block-${item.id}-${item.startsAt}`,
        title: itemTitle(item),
        description: 'Bloqueio ativo exige atenção para redistribuição.',
        meta: `${item.dayLabel || 'Agenda'} · ${formatTime(item.startsAt)} - ${formatTime(item.endsAt)}`,
        icon: 'i-lucide-calendar-off',
        tone: 'info',
        sort: 4,
      });
      return;
    }

    if (isToday) {
      rows.push({
        id: `agenda-visit-${item.id}-${item.startsAt}`,
        title: itemTitle(item),
        description: 'Visita prevista para hoje.',
        meta: `${item.dayLabel || 'Hoje'} · ${formatTime(item.startsAt)} - ${formatTime(item.endsAt)}`,
        icon: 'i-lucide-calendar-check',
        tone: 'primary',
        sort: 5,
      });
    }
  });

  return rows.sort((a, b) => a.sort - b.sort).slice(0, 16);
});
const alertSummaryCards = computed(() => [
  {
    id: 'keys',
    label: 'Chaves críticas',
    count: operationalAlerts.value.filter(alert => alert.id.startsWith('key-'))
      .length,
    icon: 'i-lucide-key-round',
  },
  {
    id: 'agenda',
    label: 'Agenda monitorada',
    count: operationalAlerts.value.filter(alert =>
      alert.id.startsWith('agenda-')
    ).length,
    icon: 'i-lucide-calendar-days',
  },
  {
    id: 'total',
    label: 'Acompanhamentos',
    count: operationalAlerts.value.length,
    icon: 'i-lucide-activity',
  },
]);
const panelTabs = computed(() =>
  CANONICAL_SECTIONS.map(section => ({
    id: section.id,
    label: section.label,
    icon: section.icon,
    badge:
      section.id === 'alertas' && operationalAlerts.value.length
        ? operationalAlerts.value.length
        : undefined,
  }))
);

const stateLabel = state =>
  ({
    to_pick_up: 'A retirar',
    toPickUp: 'A retirar',
    with_agent: 'Com corretor',
    withAgent: 'Com corretor',
    to_return: 'A devolver',
    toReturn: 'A devolver',
    returned: 'Devolvida',
    blocked: 'Bloqueada',
    not_delivered: 'Não entregue',
    notDelivered: 'Não entregue',
  })[state] || state;

const syncViewport = () => {
  isMobileViewport.value =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 639px)').matches;
};

onMounted(() => {
  syncViewport();
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncViewport);
  }
});
onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', syncViewport);
  }
});
watch(
  () => [routeValue(route.query?.section), props.view],
  () => {
    const nextSection = sectionFromRoute();
    if (nextSection !== activeSection.value) {
      activeSection.value = nextSection;
    }
  }
);
watch(activeSection, loadActiveSection, { immediate: true });
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline -->
  <section class="flex-1 overflow-auto px-4 py-5 sm:px-6 lg:px-10">
    <div
      v-if="successMessage"
      class="mb-4 rounded-lais-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-semibold text-emerald-800"
    >
      {{ successMessage }}
    </div>
    <div
      v-if="errorMessage"
      class="mb-4 rounded-lais-sm border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-semibold text-rose-800"
    >
      {{ errorMessage }}
    </div>

    <div
      class="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
    >
      <div class="min-w-0">
        <p class="text-[11px] font-bold uppercase text-lais-text-muted">
          Visitas NOVO
        </p>
        <h1
          class="mt-1 font-redHatDisplay text-[22px] font-semibold text-lais-text"
        >
          {{ activeSectionConfig.label }}
        </h1>
        <p
          class="mt-1 max-w-[720px] text-[13px] leading-relaxed text-lais-text-muted"
        >
          {{ activeSectionConfig.description }}
        </p>
        <p
          v-if="activeSectionUpdatedLabel"
          class="mt-2 text-[12px] font-semibold text-lais-text-muted"
        >
          Atualizado às {{ activeSectionUpdatedLabel }}
        </p>
      </div>
      <LaisCopyButton
        :text="activeSectionUrl"
        label="Copiar URL"
        copied-label="URL copiada"
        aria-label="Copiar URL desta seção"
      />
    </div>

    <LaisTabs
      v-model="activeSection"
      :tabs="panelTabs"
      aria-label="Seções operacionais de visitas"
      @change="selectPanelSection"
    >
      <template #agenda>
        <VisitOperationalSectionState
          section-id="agenda"
          :title="CANONICAL_SECTIONS[0].label"
          :description="CANONICAL_SECTIONS[0].description"
          :icon="CANONICAL_SECTIONS[0].icon"
          :is-loading="sectionStates.agenda.loading"
          :is-error="Boolean(sectionStates.agenda.error)"
          :is-empty="agenda.length === 0"
          :is-unauthorized="
            sectionStates.agenda.unauthorized || !canViewSection('agenda')
          "
          :error-title="CANONICAL_SECTIONS[0].errorTitle"
          :error-description="sectionStates.agenda.error"
          :empty-title="CANONICAL_SECTIONS[0].emptyTitle"
          :empty-description="CANONICAL_SECTIONS[0].emptyDescription"
          :unauthorized-title="CANONICAL_SECTIONS[0].unauthorizedTitle"
          :unauthorized-description="
            CANONICAL_SECTIONS[0].unauthorizedDescription
          "
          :retry-label="t('VISITS.COMMON.RETRY')"
          @retry="loadAgenda"
        >
          <template #actions>
            <label class="grid gap-1">
              <span
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Início
              </span>
              <input
                v-model="agendaStartDate"
                type="date"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
                @change="loadAgenda"
              />
            </label>
            <label class="grid gap-1">
              <span
                class="text-[11px] font-semibold uppercase text-lais-text-muted"
              >
                Dias
              </span>
              <select
                v-model.number="agendaDays"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
                @change="loadAgenda"
              >
                <option :value="3">3</option>
                <option :value="8">8</option>
                <option :value="14">14</option>
              </select>
            </label>
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-primary hover:border-lais-primary"
              @click="loadAgenda"
            >
              <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
              Atualizar
            </button>
          </template>
          <template #empty-actions>
            <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadAgenda">
              Atualizar agenda
            </LaisPrimaryButton>
          </template>

          <div class="grid gap-4">
            <article
              v-for="day in agenda"
              :key="day.dateId"
              class="rounded-lais border border-lais-border bg-white p-4"
            >
              <div class="flex items-center justify-between gap-3">
                <h3 class="text-[15px] font-semibold text-lais-text">
                  {{ day.label }}
                </h3>
                <span class="text-[12px] font-semibold text-lais-text-muted">
                  {{ (day.items || []).length }} item(s)
                </span>
              </div>
              <div class="mt-4 grid gap-3">
                <template v-for="row in agendaTimelineRows(day)" :key="row.id">
                  <div
                    v-if="row.type === 'now'"
                    class="flex items-center gap-3 text-[11px] font-bold uppercase text-lais-primary"
                  >
                    <span class="h-px flex-1 bg-lais-primary/30" />
                    AGORA
                    <span class="h-px flex-1 bg-lais-primary/30" />
                  </div>
                  <div v-else class="grid gap-3 sm:grid-cols-[72px_1fr]">
                    <div
                      class="pt-2 text-[12px] font-semibold text-lais-text-muted"
                    >
                      {{ hourLabel(row.hour) }}
                    </div>
                    <div class="grid gap-2">
                      <div
                        v-for="item in row.items"
                        :key="`${item.type}-${item.id}-${item.startsAt}`"
                        class="flex items-center justify-between gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-2 text-[13px]"
                      >
                        <div class="min-w-0">
                          <p class="truncate font-semibold text-lais-text">
                            {{ itemTitle(item) }}
                          </p>
                          <p class="text-[12px] text-lais-text-muted">
                            {{ formatTime(item.startsAt) }} -
                            {{ formatTime(item.endsAt) }}
                          </p>
                        </div>
                        <span
                          class="shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold"
                          :class="
                            item.type === 'block'
                              ? 'bg-amber-50 text-amber-800'
                              : 'bg-sky-50 text-sky-800'
                          "
                        >
                          {{ item.type === 'block' ? 'Bloqueio' : 'Visita' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </article>
          </div>
        </VisitOperationalSectionState>
      </template>

      <template #chaves>
        <VisitOperationalSectionState
          section-id="chaves"
          :title="CANONICAL_SECTIONS[1].label"
          :description="CANONICAL_SECTIONS[1].description"
          :icon="CANONICAL_SECTIONS[1].icon"
          :is-loading="sectionStates.chaves.loading"
          :is-error="Boolean(sectionStates.chaves.error)"
          :is-empty="flattenedKeyHandovers.length === 0"
          :is-unauthorized="
            sectionStates.chaves.unauthorized || !canViewSection('chaves')
          "
          :error-title="CANONICAL_SECTIONS[1].errorTitle"
          :error-description="sectionStates.chaves.error"
          :empty-title="CANONICAL_SECTIONS[1].emptyTitle"
          :empty-description="CANONICAL_SECTIONS[1].emptyDescription"
          :unauthorized-title="CANONICAL_SECTIONS[1].unauthorizedTitle"
          :unauthorized-description="
            CANONICAL_SECTIONS[1].unauthorizedDescription
          "
          :retry-label="t('VISITS.COMMON.RETRY')"
          @retry="loadKeys"
        >
          <template #actions>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-arrow-left"
              @click="goBackFromKeys"
            >
              Voltar
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-search"
              @click="scrollToEntrySearch"
            >
              Buscar entrada
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-info"
              @click="showKeysInfo"
            >
              Info
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-key-round"
              @click="openKeyFormModal"
            >
              Registrar retirada
            </LaisPrimaryButton>
          </template>
          <template #empty-actions>
            <div class="flex flex-wrap justify-center gap-2">
              <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadKeys">
                {{ t('VISITS.COMMON.RETRY') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                icon="i-lucide-key-round"
                @click="openKeyFormModal"
              >
                Registrar retirada
              </LaisPrimaryButton>
            </div>
          </template>

          <div class="grid gap-5">
            <PropertySuggestionCard @suggest="suggestPropertyFromKeys" />

            <EntryInfoSearchPanel
              id="entry-info-search"
              v-model="entrySearchTerm"
              :suggestions="entrySearchSuggestions"
              :error-message="entrySearchError"
              :is-loading="keyEntryInfoLoading"
              @search="searchEntryInformation"
              @select="selectEntrySuggestion"
            />

            <div class="grid gap-3 md:grid-cols-3">
              <KeySummaryCard
                v-for="card in keySummaryCards"
                :key="card.key"
                :title="card.title"
                :subtitle="card.subtitle"
                :count="card.count"
                :tone="card.tone"
                @open="scrollToKeySection(card.key)"
              />
            </div>

            <section
              v-for="group in keyGroupDefinitions"
              :id="`keys-section-${group.key}`"
              :key="group.key"
              class="scroll-mt-6 rounded-lais border border-lais-border bg-white p-4 shadow-lais-card"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 class="text-[16px] font-semibold text-lais-text">
                    {{ group.title }}
                  </h3>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ group.subtitle }}
                  </p>
                </div>
                <span
                  class="rounded-full bg-lais-bg-soft px-3 py-1 text-[12px] font-semibold text-lais-text-muted"
                >
                  {{ groupedKeys[group.key].length }} registro(s)
                </span>
              </div>
              <div
                v-if="groupedKeys[group.key].length === 0"
                class="mt-3 rounded-lais-sm border border-dashed border-lais-border px-3 py-5 text-center text-[13px] text-lais-text-muted"
              >
                {{ t('VISITS.OPERATIONAL.KEYS_EMPTY') }}
              </div>
              <div v-else class="mt-4 grid gap-4">
                <div
                  v-for="segment in keySectionSegments(group.key)"
                  :key="segment.id"
                  class="grid gap-3"
                >
                  <p
                    v-if="group.key === 'toReturn'"
                    class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
                  >
                    {{ segment.title }} · {{ segment.items.length }}
                  </p>
                  <KeyTrackingCard
                    v-for="item in segment.items"
                    :key="item.id"
                    :item="item"
                    :title="keyTitle(item)"
                    :address="keyAddress(item)"
                    :owner-name="keyOwnerName(item)"
                    :image-url="keyImageUrl(item)"
                    :code="String(keyShortCodeForItem(item))"
                    :due-label="formatDateTime(keyDue(item))"
                    :urgency-label="keyUrgency(item).label"
                    :status-label="stateLabel(item.state)"
                    :state="item.state"
                    :is-loading="
                      Boolean(keyActionLoadingId(item)) &&
                      keyActionLoading === keyActionLoadingId(item)
                    "
                    @pickup="pickupKey(item.id)"
                    @return="returnKey(item.id)"
                    @actions="openKeyActions(item)"
                    @entry="openEntryInfoForKey(item)"
                  />
                </div>
              </div>
            </section>
          </div>
        </VisitOperationalSectionState>
      </template>

      <template #alertas>
        <VisitOperationalSectionState
          section-id="alertas"
          :title="CANONICAL_SECTIONS[2].label"
          :description="CANONICAL_SECTIONS[2].description"
          :icon="CANONICAL_SECTIONS[2].icon"
          :is-loading="sectionStates.alertas.loading"
          :is-error="Boolean(sectionStates.alertas.error)"
          :is-empty="operationalAlerts.length === 0"
          :is-unauthorized="
            sectionStates.alertas.unauthorized || !canViewSection('alertas')
          "
          :error-title="CANONICAL_SECTIONS[2].errorTitle"
          :error-description="sectionStates.alertas.error"
          :empty-title="CANONICAL_SECTIONS[2].emptyTitle"
          :empty-description="CANONICAL_SECTIONS[2].emptyDescription"
          :unauthorized-title="CANONICAL_SECTIONS[2].unauthorizedTitle"
          :unauthorized-description="
            CANONICAL_SECTIONS[2].unauthorizedDescription
          "
          :retry-label="t('VISITS.COMMON.RETRY')"
          @retry="loadAlerts"
        >
          <template #actions>
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-primary hover:border-lais-primary"
              @click="loadAlerts"
            >
              <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
              Atualizar alertas
            </button>
          </template>
          <template #empty-actions>
            <LaisPrimaryButton icon="i-lucide-refresh-cw" @click="loadAlerts">
              Atualizar alertas
            </LaisPrimaryButton>
          </template>

          <div class="grid gap-4">
            <div class="grid gap-3 md:grid-cols-3">
              <article
                v-for="card in alertSummaryCards"
                :key="card.id"
                class="rounded-lais border border-lais-border bg-white p-4 shadow-lais-card"
              >
                <div class="flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <p class="text-[12px] font-semibold text-lais-text-muted">
                      {{ card.label }}
                    </p>
                    <p class="mt-1 text-[24px] font-semibold text-lais-text">
                      {{ card.count }}
                    </p>
                  </div>
                  <span
                    class="grid size-10 place-items-center rounded-lais bg-lais-bg-soft text-lais-primary"
                    :class="card.icon"
                    aria-hidden="true"
                  />
                </div>
              </article>
            </div>

            <article
              v-for="alert in operationalAlerts"
              :key="alert.id"
              class="flex flex-col gap-3 rounded-lais border border-lais-border bg-white p-4 shadow-lais-card sm:flex-row sm:items-center sm:justify-between"
            >
              <div class="flex min-w-0 gap-3">
                <span
                  class="grid size-10 shrink-0 place-items-center rounded-lais"
                  :class="[
                    alert.icon,
                    alert.tone === 'critical'
                      ? 'bg-red-50 text-red-700'
                      : alert.tone === 'warning'
                        ? 'bg-amber-50 text-amber-800'
                        : alert.tone === 'primary'
                          ? 'bg-sky-50 text-sky-800'
                          : 'bg-lais-bg-soft text-lais-primary',
                  ]"
                  aria-hidden="true"
                />
                <div class="min-w-0">
                  <p class="font-semibold text-lais-text">{{ alert.title }}</p>
                  <p class="mt-1 text-[13px] text-lais-text-body">
                    {{ alert.description }}
                  </p>
                  <p class="mt-1 text-[12px] text-lais-text-muted">
                    {{ alert.meta }}
                  </p>
                </div>
              </div>
              <LaisPrimaryButton
                v-if="alert.action"
                size="sm"
                variant="ghost"
                icon="i-lucide-arrow-right"
                @click="alert.action"
              >
                Acompanhar
              </LaisPrimaryButton>
            </article>
          </div>
        </VisitOperationalSectionState>
      </template>

      <template #recuperacao>
        <VisitOperationalSectionState
          section-id="recuperacao"
          :title="CANONICAL_SECTIONS[3].label"
          :description="CANONICAL_SECTIONS[3].description"
          :icon="CANONICAL_SECTIONS[3].icon"
          :is-loading="sectionStates.recuperacao.loading"
          :is-error="Boolean(sectionStates.recuperacao.error)"
          :is-empty="blocks.length === 0"
          :is-unauthorized="
            sectionStates.recuperacao.unauthorized ||
            !canViewSection('recuperacao')
          "
          :error-title="CANONICAL_SECTIONS[3].errorTitle"
          :error-description="sectionStates.recuperacao.error"
          :empty-title="CANONICAL_SECTIONS[3].emptyTitle"
          :empty-description="CANONICAL_SECTIONS[3].emptyDescription"
          :unauthorized-title="CANONICAL_SECTIONS[3].unauthorizedTitle"
          :unauthorized-description="
            CANONICAL_SECTIONS[3].unauthorizedDescription
          "
          :retry-label="t('VISITS.COMMON.RETRY')"
          @retry="loadBlocks"
        >
          <template #actions>
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-lais border border-lais-border px-3 text-[13px] font-semibold text-lais-primary hover:border-lais-primary"
              @click="openBlockInfoModal"
            >
              <span class="i-lucide-info size-4" aria-hidden="true" />
              Info
            </button>
            <LaisPrimaryButton
              icon="i-lucide-calendar-plus"
              :disabled="agendaSuspended"
              @click="openBlockModal"
            >
              Criar bloqueio
            </LaisPrimaryButton>
          </template>
          <template #empty-actions>
            <LaisPrimaryButton
              icon="i-lucide-calendar-plus"
              :disabled="agendaSuspended"
              @click="openBlockModal"
            >
              Criar bloqueio
            </LaisPrimaryButton>
          </template>

          <div class="grid gap-5">
            <Banner v-if="agendaSuspended" color="amber">
              <p class="font-semibold">
                Agenda suspensa. Novos bloqueios ficam desativados até o suporte
                reativar o contrato de horários.
              </p>
            </Banner>

            <div class="grid gap-3">
              <div
                class="inline-flex w-fit rounded-lais border border-lais-border bg-white p-1"
              >
                <button
                  v-for="tab in blockTabOptions"
                  :key="tab.id"
                  type="button"
                  class="rounded-lais-sm px-3 py-1.5 text-[13px] font-semibold"
                  :class="
                    activeBlockTab === tab.id
                      ? 'bg-lais-primary text-white'
                      : 'text-lais-text-muted hover:text-lais-primary'
                  "
                  @click="setBlockTab(tab.id)"
                >
                  {{ tab.label }}
                </button>
              </div>
              <Banner
                v-if="
                  activeBlockTab === 'recurrent' && !canCreateRecurrentBlock
                "
                color="blue"
              >
                <p>
                  Para adicionar um novo bloqueio recorrente, entre em contato
                  com o suporte. Esta aba é apenas para consulta.
                </p>
              </Banner>
              <EmptyStateLayout
                v-if="visibleBlocks.length === 0"
                :title="
                  activeBlockTab === 'recurrent'
                    ? t('VISITS.OPERATIONAL.BLOCKS_EMPTY_RECURRENT')
                    : t('VISITS.OPERATIONAL.BLOCKS_EMPTY_TEMPORARY')
                "
                :subtitle="
                  activeBlockTab === 'recurrent'
                    ? 'Bloqueios recorrentes são cadastrados pelo suporte.'
                    : 'Bloqueios temporários futuros aparecerão nesta aba.'
                "
                :show-backdrop="false"
              />
              <article
                v-for="block in visibleBlocks"
                :key="block.id"
                class="flex items-center justify-between gap-3 rounded-lais border border-lais-border px-4 py-3"
              >
                <div class="min-w-0">
                  <p class="font-semibold text-lais-text">
                    {{ block.reason || 'Bloqueio de agenda' }}
                  </p>
                  <p class="text-[12px] text-lais-text-muted">
                    {{ blockDateLabel(block) }} · {{ block.startTime }}-{{
                      block.endTime
                    }}
                  </p>
                </div>
                <button
                  v-if="
                    block.blockType !== 'recurrent' || canCreateRecurrentBlock
                  "
                  type="button"
                  class="grid size-9 place-items-center rounded-full border border-lais-border text-rose-700 hover:border-rose-300"
                  :aria-label="t('VISITS.ARIA.REMOVE_BLOCK')"
                  @click="requestDeleteBlock(block)"
                >
                  <span class="i-lucide-trash-2 size-4" aria-hidden="true" />
                </button>
              </article>
            </div>
          </div>
        </VisitOperationalSectionState>
      </template>
    </LaisTabs>

    <template v-if="canViewSection('chaves')">
      <LaisModal
        :show="showKeyFormModal"
        title="Confirmar retirada"
        max-width-class="max-w-[760px]"
        full-screen
        @close="closeKeyFormModal"
      >
        <form
          id="key-handover-form"
          class="grid gap-4 md:grid-cols-6"
          @submit.prevent="createKeyHandover"
        >
          <div
            class="flex flex-col gap-3 rounded-lais border border-lais-border bg-lais-bg-soft p-3 md:col-span-6 sm:flex-row sm:items-center sm:justify-between"
          >
            <div class="flex min-w-0 items-start gap-3">
              <div
                class="h-16 w-[107px] shrink-0 overflow-hidden rounded-lais-sm bg-white"
              >
                <img
                  v-if="keyImovelImageUrl"
                  :src="keyImovelImageUrl"
                  :alt="keyImovelTitle"
                  class="size-full object-cover"
                />
                <div
                  v-else
                  class="grid size-full place-items-center text-lais-primary"
                >
                  <span class="i-lucide-building-2 size-6" aria-hidden="true" />
                </div>
              </div>
              <div class="min-w-0">
                <p class="truncate text-[14px] font-semibold text-lais-text">
                  {{ keyImovelTitle }}
                </p>
                <p class="mt-1 text-[12px] font-semibold text-lais-primary">
                  Código do imóvel #{{ keyShortCode }}
                </p>
                <p class="mt-1 truncate text-[12px] text-lais-text-muted">
                  {{ keyImovelAddress }}
                </p>
              </div>
            </div>
            <LaisPrimaryButton
              size="sm"
              variant="ghost"
              icon="i-lucide-door-open"
              :is-loading="keyEntryInfoLoading"
              @click="loadKeyEntryInfo({ open: true })"
            >
              Ver acesso
            </LaisPrimaryButton>
          </div>

          <label class="grid gap-1 md:col-span-2">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Imóvel ID
            </span>
            <input
              v-model="keyForm.imovel_id"
              type="number"
              min="1"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
              @blur="() => loadKeyEntryInfo({ open: true })"
            />
          </label>
          <label class="grid gap-1 md:col-span-2">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Corretor ID
            </span>
            <input
              v-model="keyForm.user_id"
              type="number"
              min="1"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
            />
          </label>
          <label class="grid gap-1 md:col-span-1">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Qtd.
            </span>
            <input
              v-model.number="keyForm.key_quantity"
              type="number"
              min="1"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
            />
          </label>
          <label class="grid gap-1 md:col-span-1">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Tipo
            </span>
            <select
              v-model="keyForm.key_type"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
            >
              <option value="COMMON">Chave</option>
              <option value="PASSWORD">Senha</option>
              <option value="BIOMETRY">Biometria</option>
            </select>
          </label>

          <section class="grid gap-3 md:col-span-6">
            <h3 class="text-[13px] font-semibold text-lais-text">
              Quando vai pegar a chave?
            </h3>
            <div class="grid gap-3 md:grid-cols-3">
              <RadioCard
                v-for="option in keyPickupMomentOptions"
                :id="option.id"
                :key="option.id"
                :label="option.label"
                :description="option.description"
                :is-active="keyForm.pickup_moment === option.id"
                @select="value => (keyForm.pickup_moment = value)"
              />
            </div>
          </section>

          <div class="md:col-span-3">
            <DatePicker
              v-model="keyForm.picked_up_on"
              label="Quando você pegou a chave?"
              :min="keyPickupMinDate"
              :max="keyPickupMaxDate"
              required
              :message="
                keyPickedUpDateValid
                  ? 'Escolha entre hoje e até 6 dias atrás.'
                  : 'A retirada deve estar entre hoje e até 6 dias atrás.'
              "
              :message-type="keyPickedUpDateValid ? 'info' : 'error'"
            />
          </div>
          <div class="md:col-span-3">
            <DatePicker
              v-model="keyForm.due_return_on"
              label="Até quando precisa devolver?"
              :min="keyReturnMinDate"
              :max="keyReturnMaxDate"
              required
              :message="
                keyDueReturnValid
                  ? 'A data escolhida calcula o prazo de devolução em até 6 dias.'
                  : 'Escolha uma data em até 6 dias.'
              "
              :message-type="keyDueReturnValid ? 'info' : 'error'"
            />
          </div>
          <label
            class="flex items-center gap-2 rounded-lais-sm border border-lais-border-soft p-3 text-[13px] font-semibold text-lais-text md:col-span-3"
          >
            <input
              v-model="keyForm.confirm_pickup"
              type="checkbox"
              class="size-4 accent-lais-primary"
            />
            Confirmar retirada imediatamente
          </label>
          <label class="grid gap-1 md:col-span-6">
            <span
              class="text-[11px] font-semibold uppercase text-lais-text-muted"
            >
              Observação
            </span>
            <textarea
              v-model="keyForm.notes"
              rows="3"
              maxlength="255"
              class="rounded-lais border border-lais-border px-3 py-2 text-[14px]"
              placeholder="Algo a acrescentar?"
            />
            <span class="justify-self-end text-[11px] text-lais-text-muted">
              {{ keyForm.notes.length }}/255
            </span>
          </label>
        </form>
        <template #footer>
          <div class="flex flex-col gap-2 bg-white sm:flex-row sm:justify-end">
            <LaisPrimaryButton variant="ghost" @click="closeKeyFormModal">
              Sair
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-key-round"
              :is-loading="keyActionLoading === 'create' || isCheckingKeyHolder"
              :disabled="!canSubmitKeyHandover"
              @click="createKeyHandover"
            >
              Confirmar retirada das chaves
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisModal
        :show="showEntryInfoModal"
        title="Instruções de entrada"
        max-width-class="max-w-[560px]"
        @close="closeEntryInfoModal"
      >
        <div
          v-if="keyEntryInfoLoading"
          class="grid min-h-[180px] place-items-center"
        >
          <Spinner :size="32" class="text-lais-primary" />
        </div>
        <EmptyStateLayout
          v-else-if="keyEntryInfoError"
          :title="t('VISITS.OPERATIONAL.ENTRY_ERROR_TITLE')"
          :subtitle="keyEntryInfoError"
          :show-backdrop="false"
        >
          <template #actions>
            <LaisPrimaryButton
              icon="i-lucide-refresh-cw"
              @click="loadKeyEntryInfo({ open: true })"
            >
              {{ t('VISITS.COMMON.RETRY') }}
            </LaisPrimaryButton>
          </template>
        </EmptyStateLayout>
        <EmptyStateLayout
          v-else-if="!hasEntryInfoDetails"
          title="Acesso não cadastrado"
          subtitle="Este imóvel ainda não tem tipo de acesso, instruções ou contato responsável."
          :show-backdrop="false"
        />
        <div v-else class="grid gap-4">
          <div
            class="rounded-lais border border-lais-border bg-lais-bg-soft p-3"
          >
            <p class="font-semibold text-lais-text">{{ keyImovelTitle }}</p>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              {{ keyImovelAddress }}
            </p>
          </div>
          <div class="grid gap-3">
            <LaisInfoRow
              v-for="row in entryInfoRows"
              :key="row.label"
              :icon="row.icon"
              :label="row.label"
              :value="row.value"
            />
          </div>
          <div
            v-if="entryEntranceStatusLabel"
            class="flex items-center gap-2 rounded-lais border border-lais-border bg-white px-3 py-2 text-[12px] text-lais-text-body"
          >
            <span class="i-lucide-badge-check size-4 text-lais-primary" />
            <span class="font-semibold text-lais-text">Status de entrada</span>
            <span class="text-lais-text-muted">{{
              entryEntranceStatusLabel
            }}</span>
          </div>
          <div
            v-if="showEntryVisitHistory && entryVisitHistoryRows.length"
            class="grid gap-2 rounded-lais border border-lais-border bg-white p-3"
          >
            <div
              class="flex items-center gap-2 text-[12px] font-semibold text-lais-text"
            >
              <span class="i-lucide-history size-4 text-lais-primary" />
              Histórico de visitas
            </div>
            <div
              v-for="visit in entryVisitHistoryRows"
              :key="visit.id"
              class="flex items-center justify-between gap-3 border-t border-lais-border pt-2 text-[12px]"
            >
              <span class="min-w-0 truncate text-lais-text-body">
                {{ visit.scheduledAtLabel || visit.scheduled_at_label }}
              </span>
              <span class="shrink-0 font-semibold text-lais-text-muted">
                {{ visit.statusLabel || visit.status_label || visit.status }}
              </span>
            </div>
          </div>
        </div>
        <template #footer>
          <div class="flex justify-end">
            <LaisPrimaryButton @click="closeEntryInfoModal">
              Ok, entendi
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisDrawer
        :show="showKeyHolderDrawer"
        title="O imóvel já possui um corretor responsável"
        :side="keyHolderDrawerSide"
        size="sm"
        @close="showKeyHolderDrawer = false"
      >
        <div class="grid gap-4">
          <div
            class="grid size-12 place-items-center rounded-full bg-amber-50 text-amber-700"
          >
            <span class="i-lucide-lock-keyhole size-6" aria-hidden="true" />
          </div>
          <div
            class="grid gap-2 text-[13px] leading-relaxed text-lais-text-body"
          >
            <p class="font-semibold text-lais-text">
              {{ keyTitle(keyHolderBlock || {}) }}
            </p>
            <p>
              A chave já está vinculada a {{ keyUserName(keyHolderBlock) }}.
              Para evitar conflito operacional, esta retirada foi bloqueada.
            </p>
            <p class="text-lais-text-muted">
              Estado atual: {{ stateLabel(keyHolderBlock?.state) }}.
            </p>
          </div>
        </div>
        <template #footer>
          <div class="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <LaisPrimaryButton
              variant="ghost"
              @click="showKeyHolderDrawer = false"
            >
              Manter bloqueado
            </LaisPrimaryButton>
            <LaisPrimaryButton
              variant="ghost"
              icon="i-lucide-history"
              @click="openKeyCustodyDrawer(keyHolderBlock || {})"
            >
              Ver ledger
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-message-circle"
              @click="explainOwnerContactFromKey"
            >
              Conversar com proprietário
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisDrawer>

      <LaisModal
        :show="showKeyResultModal"
        :title="keyResult.title"
        max-width-class="max-w-[440px]"
        @close="showKeyResultModal = false"
      >
        <div class="grid gap-4 text-center">
          <span
            class="mx-auto grid size-12 place-items-center rounded-full"
            :class="
              keyResult.kind === 'error'
                ? 'bg-red-50 text-red-700'
                : 'bg-emerald-50 text-emerald-700'
            "
          >
            <span
              :class="
                keyResult.kind === 'error'
                  ? 'i-lucide-triangle-alert'
                  : 'i-lucide-check'
              "
              class="size-5"
              aria-hidden="true"
            />
          </span>
          <p class="text-[13px] leading-relaxed text-lais-text-body">
            {{ keyResult.description }}
          </p>
        </div>
        <template #footer>
          <div class="flex justify-end">
            <LaisPrimaryButton
              v-if="keyResult.kind === 'error'"
              icon="i-lucide-refresh-cw"
              @click="retryKeyResult"
            >
              Tentar novamente
            </LaisPrimaryButton>
            <LaisPrimaryButton v-else @click="showKeyResultModal = false">
              Ok, entendi
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisDrawer
        :show="showKeyActionsDrawer"
        title="Mais ações"
        side="bottom"
        size="sm"
        @close="showKeyActionsDrawer = false"
      >
        <div v-if="selectedKeyHandover" class="grid gap-3">
          <div
            class="rounded-lais border border-lais-border bg-lais-bg-soft p-3"
          >
            <p class="font-semibold text-lais-text">
              {{ keyTitle(selectedKeyHandover) }}
            </p>
            <p class="mt-1 text-[12px] text-lais-text-muted">
              {{ stateLabel(selectedKeyHandover.state) }} ·
              {{ formatDateTime(keyDue(selectedKeyHandover)) }}
            </p>
          </div>
          <LaisPrimaryButton
            v-if="selectedKeyHandover.state === 'to_pick_up'"
            icon="i-lucide-check"
            :is-loading="
              keyActionLoading === `pickup-${selectedKeyHandover.id}`
            "
            @click="pickupKey(selectedKeyHandover.id)"
          >
            Confirmar retirada
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="selectedKeyHandover.state === 'to_pick_up'"
            variant="ghost"
            icon="i-lucide-triangle-alert"
            :is-loading="
              keyActionLoading === `not-delivered-${selectedKeyHandover.id}`
            "
            @click="requestMarkKeyNotDelivered(selectedKeyHandover)"
          >
            Marcar como não entregue
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="selectedKeyHandover.state === 'to_return'"
            icon="i-lucide-rotate-ccw"
            :is-loading="
              keyActionLoading === `return-${selectedKeyHandover.id}`
            "
            @click="returnKey(selectedKeyHandover.id)"
          >
            Confirmar devolução
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-history"
            @click="openKeyCustodyDrawer(selectedKeyHandover)"
          >
            Abrir ledger de custódia
          </LaisPrimaryButton>
          <LaisPrimaryButton
            variant="ghost"
            icon="i-lucide-message-circle"
            @click="explainOwnerContactFromKey"
          >
            Conversar com proprietário
          </LaisPrimaryButton>
        </div>
      </LaisDrawer>

      <LaisActionReasonModal
        :show="showKeyNotDeliveredReasonModal"
        title="Marcar chave como não entregue"
        description="Registre por que a retirada não foi concluída antes de enviar a pendência para acompanhamento."
        consequence="A chave será movida para pendência operacional e o histórico ficará vinculado ao imóvel."
        reason-label="Motivo da pendência"
        reason-placeholder="Ex.: proprietário não estava no local, chave ausente, portaria sem autorização"
        acknowledge-label="Confirmo que a chave não foi entregue ao corretor."
        confirm-text="Registrar pendência"
        cancel-text="Voltar"
        :is-loading="
          Boolean(keyNotDeliveredTarget) &&
          keyActionLoading === `not-delivered-${keyNotDeliveredTarget.id}`
        "
        @close="showKeyNotDeliveredReasonModal = false"
        @confirm="markKeyNotDelivered"
      />

      <KeyCustodyDrawer
        :show="showKeyCustodyDrawer"
        :imovel-id="keyCustodyImovelId"
        :initial-asset-id="keyCustodyAssetId"
        side="right"
        @close="showKeyCustodyDrawer = false"
        @changed="handleKeyCustodyChanged"
      />
    </template>

    <template v-if="canViewSection('recuperacao')">
      <LaisModal
        :show="showBlockModal"
        title="Bloqueio temporário"
        max-width-class="max-w-[860px]"
        full-screen
        @close="showBlockModal = false"
      >
        <div class="grid gap-5">
          <Banner color="amber">
            <p class="font-semibold">
              Lembre-se: ao bloquear um dia ou horário, visitas já existentes
              serão transferidas para outro corretor ou enviadas para realocação
              operacional.
            </p>
          </Banner>

          <div class="grid gap-3 md:grid-cols-[180px_1fr]">
            <label class="grid gap-1">
              <span
                class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Corretor ID
              </span>
              <input
                v-model="blockDraft.userId"
                type="number"
                min="1"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
              />
            </label>
            <label class="grid gap-1">
              <span
                class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
              >
                Motivo
              </span>
              <input
                v-model="blockDraft.reason"
                type="text"
                placeholder="Ex.: reunião externa, almoço, plantão em outro bairro"
                class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
              />
            </label>
          </div>

          <section class="grid gap-3">
            <h3 class="text-[14px] font-semibold text-lais-text">
              Tipo de bloqueio
            </h3>
            <div class="grid gap-3 sm:grid-cols-2">
              <RadioCard
                id="temporary"
                label="Temporário"
                description="Bloqueia uma data ou um período específico."
                :is-active="blockDraft.blockType === 'temporary'"
                @select="setBlockType"
              />
              <RadioCard
                id="recurrent"
                label="Recorrente"
                description="Repete toda semana no dia escolhido."
                :disabled="!canCreateRecurrentBlock"
                disabled-label="Suporte"
                disabled-message="Para adicionar um novo bloqueio recorrente, entre em contato com o suporte."
                :is-active="blockDraft.blockType === 'recurrent'"
                @select="setBlockType"
              />
            </div>
            <Banner v-if="!canCreateRecurrentBlock" color="blue">
              <p>
                Para adicionar um novo bloqueio recorrente, entre em contato com
                o suporte. Você pode consultar recorrentes na aba própria.
              </p>
            </Banner>
            <Banner
              v-else-if="blockDraft.blockType === 'recurrent'"
              color="blue"
            >
              <p>
                Bloqueios recorrentes ficam ativos toda semana até serem
                removidos. Use para compromissos fixos do corretor.
              </p>
            </Banner>
          </section>

          <section
            v-if="blockDraft.blockType !== 'recurrent'"
            class="grid gap-3"
          >
            <h3 class="text-[14px] font-semibold text-lais-text">
              Tempo do bloqueio
            </h3>
            <div class="grid gap-3 sm:grid-cols-2">
              <RadioCard
                id="single"
                label="Um dia"
                description="Bloqueia apenas a data selecionada."
                :is-active="blockDraft.dayMode === 'single'"
                @select="setBlockDayMode"
              />
              <RadioCard
                id="multiple"
                label="Vários dias"
                description="Bloqueia o mesmo horário em um intervalo de datas."
                :is-active="blockDraft.dayMode === 'multiple'"
                @select="setBlockDayMode"
              />
            </div>

            <DatePicker
              v-if="blockDraft.dayMode === 'single'"
              v-model="blockDraft.dateRange.start"
              label="Data"
              :min="blockConfigMinDate"
              :max="blockConfigMaxDate"
              :message="`Escolha uma data entre hoje e ${blockConfigMaxDate}.`"
              required
            />
            <DateRangePicker
              v-else
              v-model="blockDraft.dateRange"
              start-label="Data inicial"
              end-label="Data final"
              :min="blockConfigMinDate"
              :max="blockConfigMaxDate"
              :start-max="multiStartMaxDate"
              :end-min="multiEndMinDate"
              :message="`Período máximo até ${blockConfigMaxDate}; a data final deve ser posterior à inicial.`"
            />
          </section>

          <section v-else class="grid gap-3">
            <h3 class="text-[14px] font-semibold text-lais-text">
              Dia recorrente
            </h3>
            <select
              v-model.number="blockDraft.weekday"
              class="h-10 rounded-lais border border-lais-border px-3 text-[14px]"
            >
              <option :value="1">Segunda-feira</option>
              <option :value="2">Terça-feira</option>
              <option :value="3">Quarta-feira</option>
              <option :value="4">Quinta-feira</option>
              <option :value="5">Sexta-feira</option>
              <option :value="6">Sábado</option>
              <option :value="0">Domingo</option>
            </select>
          </section>

          <section class="grid gap-3">
            <h3 class="text-[14px] font-semibold text-lais-text">Horários</h3>
            <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <RadioCard
                v-for="(preset, key) in blockSchedulePresets"
                :id="key"
                :key="key"
                :label="preset.label"
                :description="preset.description"
                :is-active="blockDraft.scheduleType === key"
                @select="setScheduleType"
              />
            </div>
            <div
              class="grid gap-2 rounded-lais border border-lais-border p-3 sm:grid-cols-2 lg:grid-cols-3"
            >
              <label
                v-for="slot in blockHourSlots"
                :key="slot.id"
                class="flex items-center gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-2 text-[13px] font-semibold text-lais-text"
                :class="
                  blockDraft.scheduleType !== 'custom'
                    ? 'bg-lais-bg-soft text-lais-text-muted'
                    : 'bg-white'
                "
              >
                <Checkbox
                  :model-value="isHourSelected(slot.id)"
                  :disabled="blockDraft.scheduleType !== 'custom'"
                  @change="toggleHourSlot(slot.id)"
                />
                {{ slot.label }}
              </label>
            </div>
          </section>

          <section class="grid gap-2">
            <h3 class="text-[14px] font-semibold text-lais-text">Resumo</h3>
            <AgendaTimeBlockCard :payloads="draftPreviewPayloads" />
          </section>
        </div>

        <template #footer>
          <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <LaisPrimaryButton variant="ghost" @click="showBlockModal = false">
              Cancelar
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-calendar-plus"
              :disabled="agendaSuspended"
              @click="prepareBlockSubmission"
            >
              Confirmar bloqueio
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisModal
        :show="showBlockCreateConfirm"
        title="Confirmar bloqueio"
        max-width-class="max-w-[520px]"
        @close="showBlockCreateConfirm = false"
      >
        <div class="grid gap-4 text-[13px] leading-relaxed text-lais-text-body">
          <p>
            Ao confirmar, o corretor deixará de receber visitas nos dias e
            horários selecionados:
          </p>
          <AgendaTimeBlockCard :payloads="pendingBlockPayloads" />
          <p class="font-semibold text-lais-text">{{ pendingBlockSummary }}</p>
        </div>
        <template #footer>
          <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <LaisPrimaryButton
              variant="ghost"
              @click="showBlockCreateConfirm = false"
            >
              Voltar
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-check"
              :is-loading="isSavingBlock"
              @click="createPendingBlocks()"
            >
              Confirmar
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisModal
        :show="showBlockSameDayConfirm"
        title="Bloqueio já existente"
        max-width-class="max-w-[560px]"
        @close="showBlockSameDayConfirm = false"
      >
        <div class="grid gap-4 text-[13px] leading-relaxed text-lais-text-body">
          <p>
            Você selecionou um ou mais dias que já possuem bloqueios
            temporários. Ao continuar, o novo bloqueio substituirá os existentes
            abaixo.
          </p>
          <div class="grid gap-2">
            <AgendaTimeBlockCard
              v-for="block in sameDayBlocksToReplace"
              :key="block.id"
              :block="block"
            />
          </div>
        </div>
        <template #footer>
          <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <LaisPrimaryButton
              variant="ghost"
              @click="showBlockSameDayConfirm = false"
            >
              Voltar
            </LaisPrimaryButton>
            <LaisPrimaryButton
              icon="i-lucide-check"
              :is-loading="isSavingBlock"
              @click="createPendingBlocks({ replaceSameDay: true })"
            >
              Confirmar substituição
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisModal
        :show="showBlockOverlapModal"
        title="Bloqueio já existente"
        max-width-class="max-w-[560px]"
        @close="showBlockOverlapModal = false"
      >
        <div class="grid gap-4 text-[13px] leading-relaxed text-lais-text-body">
          <p>
            Já existe um bloqueio no mesmo período selecionado. Exclua o
            bloqueio abaixo antes de continuar.
          </p>
          <div class="grid gap-2">
            <AgendaTimeBlockCard
              v-for="block in overlappingBlockMatches"
              :key="block.id"
              :block="block"
            />
          </div>
        </div>
        <template #footer>
          <div class="flex justify-end">
            <LaisPrimaryButton @click="showBlockOverlapModal = false">
              Ok
            </LaisPrimaryButton>
          </div>
        </template>
      </LaisModal>

      <LaisActionReasonModal
        :show="showBlockDeleteConfirm"
        title="Excluir bloqueio"
        :description="blockDeleteDescription"
        consequence="Esta ação reabre disponibilidade na agenda e pode permitir novos agendamentos no período removido."
        reason-label="Motivo da exclusão"
        reason-placeholder="Ex.: corretor voltou a ficar disponível, bloqueio criado por engano"
        acknowledge-label="Confirmo que revisei o período antes de excluir o bloqueio."
        confirm-text="Excluir bloqueio"
        cancel-text="Cancelar"
        :is-loading="isDeletingBlock"
        @close="showBlockDeleteConfirm = false"
        @confirm="deleteBlock"
      />

      <LaisModal
        :show="showBlockInfoModal"
        title="Disponibilidade padrão"
        max-width-class="max-w-[620px]"
        full-screen
        @close="showBlockInfoModal = false"
      >
        <div class="grid gap-4 text-[13px] leading-relaxed text-lais-text-body">
          <p>
            Os bloqueios convivem com a disponibilidade padrão de visitas. Use
            bloqueio temporário para exceções e recorrente para compromissos
            semanais fixos.
          </p>
          <div class="grid gap-2 sm:grid-cols-3">
            <div class="rounded-lais-sm border border-lais-border px-3 py-3">
              <p class="font-semibold text-lais-text">Dia inteiro</p>
              <p class="mt-1 text-lais-text-muted">
                {{ blockSchedulePresets.all_day.description }}
              </p>
            </div>
            <div class="rounded-lais-sm border border-lais-border px-3 py-3">
              <p class="font-semibold text-lais-text">Manhã</p>
              <p class="mt-1 text-lais-text-muted">
                {{ blockSchedulePresets.morning.description }}
              </p>
            </div>
            <div class="rounded-lais-sm border border-lais-border px-3 py-3">
              <p class="font-semibold text-lais-text">Tarde</p>
              <p class="mt-1 text-lais-text-muted">
                {{ blockSchedulePresets.afternoon.description }}
              </p>
            </div>
          </div>
          <div class="grid gap-2">
            <div
              v-for="row in weeklyAvailabilityRows"
              :key="row.weekday"
              class="flex items-center justify-between gap-3 rounded-lais-sm border border-lais-border-soft px-3 py-2"
            >
              <div>
                <p class="font-semibold text-lais-text">{{ row.label }}</p>
                <p class="text-[12px] text-lais-text-muted">
                  Disponibilidade padrão
                </p>
              </div>
              <p
                class="shrink-0 text-[13px] font-semibold"
                :class="
                  row.active ? 'text-lais-primary' : 'text-lais-text-muted'
                "
              >
                {{
                  row.active
                    ? `${row.startTime || row.start_time} - ${
                        row.endTime || row.end_time
                      }`
                    : 'Indisponível'
                }}
              </p>
            </div>
          </div>
          <Banner color="blue">
            <p>
              Bloqueios recorrentes são cadastrados pelo suporte e permanecem
              ativos até exclusão manual.
            </p>
          </Banner>
        </div>
      </LaisModal>
    </template>
  </section>
</template>
